// src/pages/PackagesPage.js
import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Lock,
  Check,
  ChevronRight,
  ChevronDown,
  ArrowLeft,
  Home,
  Zap,
  CreditCard,
  Wrench,
  ShieldCheck,
  Sparkles,
  HeartHandshake,
  BookOpen,
  Layers,
  Star,
  Award,
  Quote,
  Users,
  Settings,
  X,
  Download,
  FileText,
  Eye,
  EyeOff,
} from 'lucide-react';

const ACCESS_PASSWORD = 'maryam27';
const DEFAULT_STUDENTS = 1000; // purata sekolah
const MAX_STUDENTS = 3000; // had slider

// --- Tiered pricing (4 band, berasaskan bilangan murid) ---
//   Tier 0: < 300 (Rahmah Eksklusif) · Tier 1: 300–800 (Rahmah)
//   Tier 2: 801–1,700 (Standard) · Tier 3: > 1,700 (Premier)
const TIER_BREAKS = [300, 800, 1700]; // upper bounds for tiers 0,1,2

// shades — light → dark per tier (Rahmah Eksklusif = teal accent)
const TIER_COLORS = ['#5eead4', '#86efac', '#22c55e', '#15803d'];

const TIERS = [
  { key: 1, label: 'Rahmah Eksklusif', range: `< ${TIER_BREAKS[0]} murid`, color: TIER_COLORS[0] },
  { key: 2, label: 'Sekolah Rahmah', range: `${TIER_BREAKS[0]} – ${TIER_BREAKS[1]} murid`, color: TIER_COLORS[1] },
  { key: 3, label: 'Sekolah Standard', range: `${TIER_BREAKS[1] + 1} – ${TIER_BREAKS[2]} murid`, color: TIER_COLORS[2] },
  { key: 4, label: 'Sekolah Premier', range: `> ${TIER_BREAKS[2]} murid`, color: TIER_COLORS[3] },
];
const DEFAULT_TIER = 2; // index → Sekolah Standard

const tierIndexFor = (students) => {
  if (students < TIER_BREAKS[0]) return 0;
  if (students <= TIER_BREAKS[1]) return 1;
  if (students <= TIER_BREAKS[2]) return 2;
  return 3;
};

// --- Editable pricing config (defaults). Admin panel can override these live. ---
// All tier arrays are 4 values: [Rahmah Eksklusif, Rahmah, Standard, Premier]
const DEFAULT_CONFIG = {
  sistemInitialTiers: [2000, 2700, 3400, 3900], // tahun pertama (Premium account)
  bukuInitialTiers: [3200, 3900, 4700, 5700],
  // Mod langganan sistem: 'fixed' = kadar tetap tahunan ikut tier; 'murid' = per murid.
  renewalMode: 'fixed',
  sistemRenewalFixedTiers: [300, 500, 900, 1100], // kadar tetap tahunan ikut tier
  sistemRenewalPerStudent: 1.2, // RM/murid (mod 'murid')
  sistemRenewalMin: 200, // bayaran minimum per murid
  sistemRenewalMax: 8000, // had maksimum
  bukuRenewalTiers: [1200, 2000, 3500, 4500], // beli buku, tahun seterusnya (Premium)
  // Digitera — sistem kawalan sekolah (produk berasingan)
  digiteraInitialTiers: [2500, 3200, 3900, 4500], // tahun pertama, ikut tier
  digiteraInitialOriginal: 7000, // harga asal (potong) tahun pertama
  digiteraRenewalTiers: [400, 600, 1000, 1200], // langganan tahunan, ikut tier
  // EzBook — sistem tempahan bilik (produk berasingan)
  ezbookInitialTiers: [1200, 1500, 1900, 2300], // tahun pertama, ikut tier
  ezbookInitialOriginal: 3500, // harga asal (potong) tahun pertama
  ezbookRenewalTiers: [300, 400, 600, 800], // langganan tahunan, ikut tier
  // "Harga asal" untuk paparan potong harga (anchor). 0 = tiada potongan.
  sistemInitialOriginal: 6500, // harga asal beli sistem tahun pertama
  renewalPerStudentOriginal: 3, // harga asal RM/murid tahun seterusnya
  scannerQty: 1, // bilangan Scanner 2D yang sekolah dapat (cth: "2× Scanner 2D")
  // Promosi Pakej Buku — potong harga tahun pertama (Pakej Buku sahaja).
  promoEnabled: false, // toggle di admin panel
  promoName: 'Promosi Pakej Buku Pertengahan Tahun',
  promoCut: 0, // RM ditolak dari harga tahun pertama setiap tier (Pakej Buku)
  // Harga kad matrik — { ori: harga asal (potong), now: harga istimewa }
  kadPrices: {
    berlogo: { ori: 13, now: 11 },
    bergambar: { ori: 15, now: 13 },
    laminate: { ori: 4, now: 4 },
    pokettali: { ori: 2.5, now: 2.5 },
  },
};

const safeStudents = (n) => {
  const v = Number(n);
  return Number.isFinite(v) && v > 0 ? Math.floor(v) : 0;
};

// Apply live-config substitutions to a static feature-item string.
// Currently: "N× Scanner 2D" quantity follows cfg.scannerQty.
const applyItemConfig = (item, cfg = DEFAULT_CONFIG) =>
  /Scanner 2D/i.test(item)
    ? item.replace(/^\d+×\s*/, `${cfg.scannerQty ?? 1}× `)
    : item;

// Returns every price using the given config.
//  - studentsRaw drives the per-murid renewal (always actual count)
//  - selectedTier (0..3) locks the tier; null = auto from murid ("Lihat Semua").
//
// Renewal = langganan: PREMIUM (penuh) atau BASIC (separuh harga, ciri terhad).
// Rahmah Eksklusif BASIC = PERCUMA.
const computePrices = (studentsRaw, selectedTier = null, cfg = DEFAULT_CONFIG) => {
  const s = safeStudents(studentsRaw);
  const ti = selectedTier == null ? tierIndexFor(s) : selectedTier;
  const isRahmahEksklusif = ti === 0;

  // Renewal mode: 'fixed' = kadar tetap tahunan ikut tier; 'murid' = per murid.
  const renewalMode = cfg.renewalMode === 'murid' ? 'murid' : 'fixed';
  const isFixedRenewal = renewalMode === 'fixed';

  // Per-murid renewal (used when mode = 'murid')
  const muridPerStudent = Math.min(
    Math.max(s * cfg.sistemRenewalPerStudent, cfg.sistemRenewalMin),
    cfg.sistemRenewalMax
  );
  const fixedTiers = cfg.sistemRenewalFixedTiers || [300, 500, 900, 1100];
  // Premium renewal base — the active "sistem" subscription value for this tier.
  const muridPremium = isFixedRenewal ? fixedTiers[ti] : muridPerStudent;
  const bukuPremium = cfg.bukuRenewalTiers[ti];
  const digiteraPremium = (cfg.digiteraRenewalTiers || [400, 600, 1000, 1200])[ti];
  const ezbookPremium = (cfg.ezbookRenewalTiers || [300, 400, 600, 800])[ti];

  // Basic = half (Rahmah Eksklusif = free)
  const muridBasic = isRahmahEksklusif ? 0 : Math.round(muridPremium / 2);
  const bukuBasic = isRahmahEksklusif ? 0 : Math.round(bukuPremium / 2);
  const digiteraBasic = isRahmahEksklusif ? 0 : Math.round(digiteraPremium / 2);
  const ezbookBasic = isRahmahEksklusif ? 0 : Math.round(ezbookPremium / 2);

  // Promosi Pakej Buku — potong `promoCut` dari harga tahun pertama (tier).
  // bukuInitialBase = harga tier asal; bukuInitial = harga selepas promo.
  const bukuInitialBase = cfg.bukuInitialTiers[ti];
  const promoCut = Math.max(0, Number(cfg.promoCut) || 0);
  const promoActive = Boolean(cfg.promoEnabled) && promoCut > 0;
  const bukuInitial = promoActive
    ? Math.max(0, bukuInitialBase - promoCut)
    : bukuInitialBase;

  return {
    tierIndex: ti,
    tier: TIERS[ti],
    autoTier: selectedTier == null,
    isRahmahEksklusif,
    sistemInitial: cfg.sistemInitialTiers[ti],
    bukuInitial,
    bukuInitialBase,
    digiteraInitial: (cfg.digiteraInitialTiers || [2500, 3200, 3900, 4500])[ti],
    ezbookInitial: (cfg.ezbookInitialTiers || [1200, 1500, 1900, 2300])[ti],
    promoActive,
    promoCut,
    promoName: cfg.promoName || 'Promosi Pakej Buku',
    // renewal
    renewalMode,
    muridPremium,
    muridBasic,
    bukuPremium,
    bukuBasic,
    digiteraPremium,
    digiteraBasic,
    ezbookPremium,
    ezbookBasic,
    // floor/cap only meaningful in per-murid mode
    sistemRenewalCapped: !isFixedRenewal && s * cfg.sistemRenewalPerStudent >= cfg.sistemRenewalMax,
    sistemRenewalFloored: !isFixedRenewal && s * cfg.sistemRenewalPerStudent <= cfg.sistemRenewalMin,
  };
};

const CONTACT_WA = 'https://wa.me/60173634766';
const LOGO_URL =
  'https://ehsfztlgxjkdeuobjttw.supabase.co/storage/v1/object/public/others/zabra%20logo.png';

const ACCENT = '#7c3aed'; // exclusive purple
const ACCENT_LIGHT = '#7c3aed'; // used as icon tint inside white cards — keep readable on white

const formatRM = (n) => {
  const v = Number.isFinite(Number(n)) ? Number(n) : 0;
  return 'RM' + v.toLocaleString('en-MY', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

// Tahun-pertama price / anchor / note for any package — one source of truth for
// PackageCard, PackageScene and the quote export. Uses the package's `pricing`
// descriptor so a new product just declares its keys.
const packageInitial = (pkg, prices, config) => {
  const pr = pkg.pricing || {};
  if (pkg.id === 'buku') {
    return {
      price: prices.bukuInitial,
      original: prices.promoActive ? prices.bukuInitialBase : config.bukuInitialTiers[2],
      note: prices.promoActive ? prices.promoName : 'NILAI buku · sistem automasi PERCUMA',
    };
  }
  const price = prices[pr.initialKey];
  const original = pr.originalConfig ? config[pr.originalConfig] : 0;
  return { price, original, note: '' };
};

// A "solo product" is a standalone single-product package (Digitera, EzBook) —
// one tier-based tahun-pertama price + one annual subscription. Litera's sistem
// & buku are NOT solo (they share the murid/buku renewal streams).
const SOLO_PRODUCTS = ['digitera', 'ezbook'];
const isSoloProduct = (pkg) => SOLO_PRODUCTS.includes(pkg.id);
// The active solo product's annual renewal value.
const soloRenewalValue = (pkg, prices) =>
  pkg.id === 'ezbook' ? prices.ezbookPremium : prices.digiteraPremium;
const soloRenewalBasic = (pkg, prices) =>
  pkg.id === 'ezbook' ? prices.ezbookBasic : prices.digiteraBasic;

/* =========================================================================
   PASSWORD GATE
   ========================================================================= */
const PasswordGate = ({ onUnlock }) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (value === ACCESS_PASSWORD) onUnlock();
    else {
      setError(true);
      setValue('');
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-gradient-to-br from-[#2e1065] via-[#4c1d95] to-[#3b0764] px-4">
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-sm bg-white border border-black/10 rounded-2xl p-8 shadow-xl"
      >
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-full bg-[#7c3aed]/15 flex items-center justify-center">
            <Lock className="text-[#6d28d9]" size={28} />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-[#14110d] text-center mb-2">Kawasan Sulit</h1>
        <p className="text-[#5c554d] text-center text-base mb-6">
          Masukkan kata laluan untuk melihat pakej.
        </p>
        <form onSubmit={handleSubmit}>
          <input
            type="password"
            autoFocus
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setError(false);
            }}
            placeholder="Kata laluan"
            className={`w-full px-4 py-3 rounded-lg bg-white text-[#14110d] placeholder-gray-400 outline-none border transition ${
              error ? 'border-red-500' : 'border-black/10 focus:border-[#7c3aed]'
            }`}
          />
          <AnimatePresence>
            {error && (
              <motion.p
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="text-red-400 text-base mt-2"
              >
                Kata laluan salah. Cuba lagi.
              </motion.p>
            )}
          </AnimatePresence>
          <button
            type="submit"
            className="w-full mt-5 bg-[#7c3aed] hover:bg-[#6d28d9] text-white font-semibold py-3 rounded-lg transition flex items-center justify-center gap-2"
          >
            Buka <ChevronRight size={18} />
          </button>
        </form>
      </motion.div>
    </div>
  );
};

/* =========================================================================
   DATA
   ========================================================================= */
const PACKAGES = {
  sistem: {
    id: 'sistem',
    name: 'Pakej Sistem',
    subname: 'Sistem Pusat Sumber Digital',
    tagline: 'Naik taraf terus — tanpa pembelian buku',
    price: 'RM2,800',
    priceNote: 'Pelaburan permulaan · sekali bayar',
    recommended: false,
    icon: Layers,
    theme: { from: '#7c3aed', to: '#4c1d95', light: '#7c3aed', glow: '#7c3aed' },
    // pricing descriptor — which computePrices/config keys drive this package.
    // renewal: 'sistem' = per-murid/fixed mode; 'tiers' = own renewal tier array.
    pricing: { initialKey: 'sistemInitial', originalConfig: 'sistemInitialOriginal', renewal: 'sistem' },
    diff: 'Pilihan jimat. Sesuai untuk sekolah yang ingin membeli sistem terus melalui PCG atau PIBG',
    // Berdasarkan flyer 2026 — apa yang sekolah dapat.
    groups: [
      {
        title: 'PERCUMA · Produk',
        items: [
          '1× Sistem Pinjam Pulang',
          '1× Sistem Kehadiran',
          '1× Akaun Admin',
          '1× Akaun Kaunter',
          '1× Akaun Kehadiran',
          '1× Scanner 2D',
        ],
      },
      {
        title: 'PERCUMA · Servis / Konsultan',
        items: [
          'Servis Cloud (pengkomputeran awan)',
          'Servis Baiki Komputer',
          'Sesi Latihan Kegunaan Sistem',
          'Pemasangan & Setup',
          'Backup & Pemindahan Data',
        ],
      },
      {
        title: 'PERCUMA · Data Servis',
        items: [
          'Convert & Clean Data dari sistem lama',
          'Masukkan Data Murid terkini (siap diguna)',
          'System Upgrade (mengikut feedback Guru Media & Jabatan)',
        ],
      },
    ],
  },
  buku: {
    id: 'buku',
    name: 'Pakej Buku',
    subname: 'PERCUMA Sistem Pusat Sumber Digital',
    tagline: 'Sistem PERCUMA dengan pembelian buku melalui PCG',
    price: 'RM4,700',
    priceNote: 'Nilai buku · sistem automasi PERCUMA',
    recommended: false,
    icon: BookOpen,
    theme: { from: '#0ea5e9', to: '#155e75', light: '#0ea5e9', glow: '#0ea5e9' },
    // buku: promo-aware; renewal via its own tier array.
    pricing: { initialKey: 'bukuInitial', renewal: 'buku', hasPromo: true },
    diff: 'Bijak untuk peruntukan PCG buku — tambah koleksi PSS anda, dan dapatkan sistem automasi penuh.',
    groups: [
      {
        title: 'Buku Bernilai RM4,700',
        items: [
          'Buku dari katalog terkini',
          'Tidak perlu kertas kerja',
          'Buku dibeli siap diproses',
        ],
      },
      {
        title: 'PERCUMA · Produk',
        items: [
          '1× Sistem Pinjam Pulang',
          '1× Sistem Kehadiran',
          '1× Akaun Admin',
          '1× Akaun Kaunter',
          '1× Akaun Kehadiran',
          '1× Scanner 2D',
        ],
      },
      {
        title: 'PERCUMA · Servis / Konsultan',
        items: [
          'Servis Cloud (pengkomputeran awan)',
          'Servis Check Komputer',
          'Sesi Latihan Kegunaan Sistem',
          'Pemasangan & Setup',
          'Backup & Pemindahan Data',
        ],
      },
      {
        title: 'PERCUMA · Data Servis',
        items: [
          'Convert & Clean Data dari sistem lama',
          'Masukkan Data Murid terkini (siap diguna)',
          'System Upgrade tahunan (mengikut feedback Guru Media & Jabatan)',
        ],
      },
    ],
  },
  digitera: {
    id: 'digitera',
    name: 'Digitera',
    subname: 'Sistem Kawalan Sekolah',
    tagline: 'Kawalan & pemantauan sekolah dalam satu sistem digital',
    price: 'RM3,500',
    priceNote: 'Pelaburan permulaan · sekali bayar',
    recommended: false,
    icon: ShieldCheck,
    logoImg: '/digitera-icon.png', // ikon aplikasi Digitera
    theme: { from: '#0d9488', to: '#134e4a', light: '#0d9488', glow: '#0d9488' },
    // Digitera: own tahun-pertama tiers, own anchor, own renewal tier array.
    pricing: { initialKey: 'digiteraInitial', originalConfig: 'digiteraInitialOriginal', renewal: 'digitera' },
    diff: 'Sistem kawalan kehadiran guru & staf — kiosk pengganti punch card, akses mobile setiap guru, dan analisa lengkap untuk pentadbir.',
    groups: [
      {
        title: 'Kiosk — Pengganti Punch Card',
        items: [
          'Clock in/out dengan imbas Kad ID',
          'Alternatif guna kad sedia ada',
          'Tiada mesin punch card lama',
        ],
      },
      {
        title: 'Akses Mobile · Setiap Guru & Staf',
        items: [
          'Clock in/out dengan geolokasi (kawasan sekolah)',
          'Mohon cuti, pelepasan & tugas rasmi',
          'Semua dalam telefon sendiri',
        ],
      },
      {
        title: 'Akses Admin · Analisa Lengkap',
        items: [
          'Dashboard & analisa kehadiran menyeluruh',
          'Pengurusan cuti (lulus/tolak) + notifikasi',
          'Export Excel & PDF · akses PC/laptop/telefon',
        ],
      },
      {
        title: 'PERCUMA · Servis / Konsultan',
        items: [
          'Servis Cloud (pengkomputeran awan)',
          'Sesi Latihan Kegunaan Sistem',
          'Pemasangan & Setup',
          'Backup & Pemindahan Data',
        ],
      },
    ],
  },
  ezbook: {
    id: 'ezbook',
    name: 'EzBook',
    subname: 'Sistem Tempahan Bilik',
    tagline: 'Tempahan bilik & fasiliti — mudah, pantas & mesra mobile',
    price: 'RM1,500',
    priceNote: 'Pelaburan permulaan · sekali bayar',
    recommended: false,
    icon: BookOpen,
    logoImg: '/ezbook-icon.png', // ikon aplikasi EzBook
    theme: { from: '#059669', to: '#064e3b', light: '#059669', glow: '#059669' },
    pricing: { initialKey: 'ezbookInitial', originalConfig: 'ezbookInitialOriginal', renewal: 'ezbook' },
    diff: 'Sistem tempahan bilik lengkap & terbukti — admin setup mudah, kongsi pautan, kolaborator urus tempahan, orang awam lihat & mohon. Pantas & mesra mobile.',
    groups: [
      {
        title: 'Admin · Setup Mudah',
        items: [
          'Tetapkan bilik, jadual & tetapan asas',
          'Cipta akaun kolaborator',
          'Kongsi pautan awam & pautan kolaborator',
        ],
      },
      {
        title: 'Kolaborator · Urus Tempahan',
        items: [
          'Log masuk selamat (perlu pengesahan)',
          'Tambah, edit & padam tempahan',
          'Tempahan berulang (recurring) sekali klik',
        ],
      },
      {
        title: 'Orang Awam · Lihat & Mohon',
        items: [
          'Akses terus tanpa log masuk (lihat sahaja)',
          'Lihat slot kosong & mohon tempahan',
          'Kolaborator sahkan tempahan',
        ],
      },
      {
        title: 'PERCUMA · Servis / Konsultan',
        items: [
          'Servis Cloud (pengkomputeran awan)',
          'Sesi Latihan Kegunaan Sistem',
          'Pemasangan & Setup',
          'Backup & Pemindahan Data',
        ],
      },
    ],
  },
};

// Kelebihan showcase — screenshots grouped by capability (public/kelebihan/*.png)
const KELEBIHAN_SHOWCASE = [
  {
    key: 'dashboard',
    icon: Layers,
    title: 'Satu Pandangan, Semua Jelas',
    blurb:
      'Dashboard menyeluruh menunjukkan “tahap kesihatan” pusat sumber anda — keaktifan kehadiran & pinjam-pulang dalam satu paparan mudah.',
    shots: [
      { src: '/kelebihan/k01.png', cap: 'Lihat butiran pinjam-pulang buku pusat sumber.' },
      { src: '/kelebihan/k02.png', cap: 'Lihat butiran kehadiran ke pusat sumber.' },
      { src: '/kelebihan/k20.png', cap: 'Akaun kehadiran berasingan — pantau masuk/keluar murid.' },
    ],
  },
  {
    key: 'bahan',
    icon: BookOpen,
    title: 'Urus Bahan Tanpa Kerenah',
    blurb:
      'Daftar, cari & proses bahan dengan pantas — carian simple & advanced, tambah bahan 1 page, dan pintasan ISBN yang tarik data dari komuniti sekolah Zabra.',
    shots: [
      { src: '/kelebihan/k03.png', cap: 'Daftar bahan & pencarian — simple dan advanced.' },
      { src: '/kelebihan/k04.png', cap: 'Tambah bahan baru — cepat, hanya 1 muka surat.' },
      { src: '/kelebihan/k05.png', cap: 'Pintasan ISBN — tarik data dari komuniti sekolah Zabra.' },
      { src: '/kelebihan/k06.png', cap: 'Print barkod & tulang buku dengan pilihan sekolah.' },
      { src: '/kelebihan/k07.png', cap: 'Barkod standard — identiti kemas, guna kertas A4.' },
      { src: '/kelebihan/k08.png', cap: 'Barkod sticker — lebih cepat, tersusun, tak perlu potong.' },
      { src: '/kelebihan/k09.png', cap: 'Tulang buku sticker — sama mudahnya.' },
    ],
  },
  {
    key: 'pinjaman',
    icon: Zap,
    title: 'Pinjam-Pulang Sepantas Kilat',
    blurb:
      'Proses laju sehingga murid sekolah rendah pun boleh guna. Pinjam berkelompok, pulang hanya dengan imbas barkod buku, dan pantau tunggakan mengikut kelas.',
    shots: [
      { src: '/kelebihan/k10.png', cap: 'Pinjam pantas & mudah — sekolah rendah pun pandai guna.' },
      { src: '/kelebihan/k11.png', cap: 'Pinjam berkelompok — lagi pantas & mudah.' },
      { src: '/kelebihan/k12.png', cap: 'Pulang pantas — hanya imbas barkod buku, tanpa ID murid.' },
      { src: '/kelebihan/k13.png', cap: 'Bird-eye view — kenal pasti kelas yang ada tunggakan.' },
      { src: '/kelebihan/k14.png', cap: 'Terus kenal pasti murid/guru dengan cepat & tepat.' },
    ],
  },
  {
    key: 'laporan',
    icon: Award,
    title: 'Laporan Sedia Audit',
    blurb:
      'Laporan menyeluruh, graphical & boleh export — ringkasan mengikut rubrik, analisa koleksi, dan banyak lagi mengikut keperluan Guru Media.',
    shots: [
      { src: '/kelebihan/k15.png', cap: 'Ringkasan rubrik — menyeluruh, graphical & boleh export.' },
      { src: '/kelebihan/k16.png', cap: 'Analisa koleksi — antara banyak laporan mengikut keperluan.' },
    ],
  },
  {
    key: 'penilaian',
    icon: ShieldCheck,
    title: 'Penilaian Kendiri Selaras KPM',
    blurb:
      'Instrumen Penarafan Kendiri Pusat Sumber — diminta oleh pegawai PSS negeri & selaras kehendak KPM. Sistem kira automatik bahagian yang ada data, dan tandakan dengan jelas bahagian yang perlu diisi oleh Guru Media.',
    shots: [
      { src: '/kelebihan/k21.png', cap: 'Item dikira automatik vs perlu diisi manual — jelas & teratur, siap dengan bukti data.' },
      { src: '/kelebihan/k22.png', cap: 'Muat turun sebagai Excel — format sama seperti keperluan SSTP/instrumen negeri.' },
    ],
  },
  {
    key: 'anugerah',
    icon: Sparkles,
    title: 'Raikan Murid Terbaik',
    blurb:
      'Papan markah (leaderboard) untuk kehadiran & pinjaman, lengkap dengan cetakan sijil sedia guna — sekali dengan bukti aktiviti.',
    shots: [
      { src: '/kelebihan/k17.png', cap: 'Papan markah — ranking kehadiran & pinjaman, cepat & tepat.' },
      { src: '/kelebihan/k18.png', cap: 'Cetak sijil — guna kertas sijil pilihan sekolah, tanpa edit.' },
      { src: '/kelebihan/k19.png', cap: 'Sijil + bukti aktiviti dalam satu cetakan.' },
    ],
  },
];

// Digitera showcase — sistem kawalan sekolah, 3 topik utama.
const DIGITERA_SHOWCASE = [
  {
    key: 'kiosk',
    icon: CreditCard,
    title: 'Kiosk — Pengganti Punch Card',
    blurb:
      'Gantikan mesin punch card lama. Guru & staf clock in/out hanya dengan imbas Kad ID di kiosk sekolah — pantas, tiada kad rosak, tiada rekod hilang.',
    shots: [
      { src: '/kelebihan/digitera/kiosk-1.png', cap: 'Kiosk kehadiran — imbas Kad ID untuk clock in/out. Boleh juga guna kad sedia ada.' },
    ],
  },
  {
    key: 'mobile',
    icon: ShieldCheck,
    portrait: true, // skrin telefon (potret)
    title: 'Akses Mobile untuk Setiap Guru & Staf',
    blurb:
      'Setiap guru/staf ada akses melalui telefon sendiri — clock in/out dengan geolokasi (mesti dalam kawasan sekolah), mohon cuti, pelepasan & tugas rasmi, semuanya dalam telefon.',
    shots: [
      { src: '/kelebihan/digitera/mobile-1.png', cap: 'Log masuk melalui telefon — akses peribadi setiap guru & staf.' },
      { src: '/kelebihan/digitera/mobile-2.png', cap: 'Clock in/out, mohon cuti & tugas rasmi — semuanya dalam telefon.' },
      { src: '/kelebihan/digitera/mobile-5.png', cap: 'Geolokasi — hanya boleh clock in/pelepasan dalam kawasan sekolah.' },
      { src: '/kelebihan/digitera/mobile-9.png', cap: 'Di luar kawasan sekolah tidak boleh clock in / pelepasan.' },
      { src: '/kelebihan/digitera/mobile-7.png', cap: 'Pelepasan — mohon keluar sementara dengan mudah.' },
      { src: '/kelebihan/digitera/mobile-8.png', cap: 'Pelepasan — pilihan sebab yang tersedia.' },
      { src: '/kelebihan/digitera/mobile-10.png', cap: 'Clock out auto-detect pada waktu keluar yang ditetapkan.' },
      { src: '/kelebihan/digitera/mobile-11.png', cap: 'Kembali — mesti berada dalam kawasan sekolah semula.' },
      { src: '/kelebihan/digitera/mobile-3.png', cap: 'Mohon cuti mengikut standard ekosistem sekolah.' },
      { src: '/kelebihan/digitera/mobile-4.png', cap: 'Tugas rasmi mengikut standard ekosistem sekolah.' },
      { src: '/kelebihan/digitera/mobile-6.png', cap: 'Segala pengurusan kawalan boleh diakses melalui telefon sahaja.' },
    ],
  },
  {
    key: 'admin',
    icon: Layers,
    title: 'Akses Admin / Pentadbir — Analisa Lengkap',
    blurb:
      'Pentadbir akses melalui telefon, PC atau laptop untuk analisa menyeluruh — dashboard, analisa kehadiran, cuti tahunan, pengurusan cuti (lulus/tolak) dengan notifikasi, dan tetapan waktu ikut jawatan. Boleh export Excel & PDF.',
    shots: [
      { src: '/kelebihan/digitera/admin-1.png', cap: 'Log masuk pentadbir melalui PC / laptop.' },
      { src: '/kelebihan/digitera/admin-2.png', cap: 'Dashboard — pandangan menyeluruh kehadiran sekolah.' },
      { src: '/kelebihan/digitera/admin-3.png', cap: 'Analisa kehadiran guru — menyeluruh & teratur.' },
      { src: '/kelebihan/digitera/admin-8.png', cap: 'Analisa kehadiran guru — boleh export Excel & PDF.' },
      { src: '/kelebihan/digitera/admin-4.png', cap: 'Analisa terperinci — masuk, keluar, cuti, pelepasan & tugas rasmi.' },
      { src: '/kelebihan/digitera/admin-5.png', cap: 'Analisa cuti tahunan — juga boleh export Excel.' },
      { src: '/kelebihan/digitera/admin-6.png', cap: 'Pengurusan cuti (lulus/tolak) dengan notifikasi.' },
      { src: '/kelebihan/digitera/admin-7.png', cap: 'Tetapan waktu masuk/keluar mengikut hari & jawatan.' },
      { src: '/kelebihan/digitera/admin-9.png', cap: 'Tetapan waktu — boleh kustom hari berbeza.' },
    ],
  },
];

// EzBook showcase — sistem tempahan bilik, 3 topik: Admin → Kolaborator → Awam.
const EZBOOK_SHOWCASE = [
  {
    key: 'admin',
    icon: Settings,
    title: 'Admin · Setup Mudah',
    blurb:
      'Admin sediakan asas sekali sahaja — tetapkan bilik, jadual & tetapan, cipta akaun kolaborator, kemudian kongsi pautan awam & pautan kolaborator. Boleh diakses PC/laptop atau telefon.',
    shots: [
      { src: '/kelebihan/ezbook/admin-1.png', cap: 'Tetapan bilik / fasiliti.' },
      { src: '/kelebihan/ezbook/admin-2.png', cap: 'Tetapan jadual & waktu.' },
      { src: '/kelebihan/ezbook/admin-3.png', cap: 'Cipta & urus akaun kolaborator.' },
      { src: '/kelebihan/ezbook/admin-4.png', cap: 'Kongsi pautan awam & pautan kolaborator.' },
      { src: '/kelebihan/ezbook/admin-5.png', cap: 'Senarai tempahan — pandangan admin.' },
      { src: '/kelebihan/ezbook/admin-6.png', cap: 'Laporan tempahan.' },
    ],
  },
  {
    key: 'admin-mobile',
    icon: Settings,
    portrait: true,
    phone: 'se', // iPhone SE (klasik, butang home)
    title: 'Admin · Dalam Telefon',
    blurb: 'Segala pengurusan admin juga boleh dibuat terus dari telefon — termasuk tempahan baru & tempahan berulang.',
    shots: [
      { src: '/kelebihan/ezbook/admin-m1.png', cap: 'Papan pemuka admin (mobile).' },
      { src: '/kelebihan/ezbook/admin-m2.png', cap: 'Urus tempahan dari telefon.' },
      { src: '/kelebihan/ezbook/admin-m3.png', cap: 'Tempahan baru — pantas.' },
      { src: '/kelebihan/ezbook/admin-m4.png', cap: 'Pintasan tempahan berulang (recurring).' },
    ],
  },
  {
    key: 'collaborator',
    icon: ShieldCheck,
    portrait: true,
    phone: 'se',
    title: 'Kolaborator · Urus Tempahan',
    blurb:
      'Kolaborator log masuk (perlu pengesahan) melalui pautan kolaborator, kemudian tambah, edit & padam tempahan. Merekalah yang mengesahkan permohonan tempahan orang awam.',
    shots: [
      { src: '/kelebihan/ezbook/collab-1.png', cap: 'Kolaborator urus tempahan.' },
      { src: '/kelebihan/ezbook/collab-2.png', cap: 'Log masuk kolaborator (perlu pengesahan).' },
    ],
  },
  {
    key: 'public',
    icon: Users,
    portrait: true,
    phone: 'se',
    title: 'Orang Awam · Lihat & Mohon',
    blurb:
      'Orang awam guna pautan awam — akses terus tanpa log masuk, lihat slot kosong. Untuk menempah, mereka mohon dan kolaborator akan sahkan tempahan. Mudah, pantas & mesra mobile.',
    shots: [
      { src: '/kelebihan/ezbook/public-1.png', cap: 'Paparan awam — lihat sahaja, tanpa log masuk.' },
    ],
  },
];

// Per solo-product metadata for the Kelebihan scene + quote lampiran.
const SOLO_QUOTE_META = {
  digitera: {
    showcase: DIGITERA_SHOWCASE,
    kelebihanTitle: 'Kelebihan Digitera',
    kelebihanSub: 'Sistem kawalan kehadiran guru & staf',
    intro:
      'Sistem kawalan kehadiran guru & staf — kiosk pengganti punch card, akses mobile setiap guru, dan analisa lengkap untuk pentadbir. Terokai paparan sebenar di bawah.',
  },
  ezbook: {
    showcase: EZBOOK_SHOWCASE,
    kelebihanTitle: 'Kelebihan EzBook',
    kelebihanSub: 'Sistem tempahan bilik — admin, kolaborator & orang awam',
    intro:
      'Sistem tempahan bilik lengkap & terbukti — admin setup mudah, kongsi pautan, kolaborator urus tempahan, orang awam lihat & mohon. Terokai paparan sebenar di bawah.',
  },
};

const SERVIS = [
  { icon: Wrench, title: 'Kami Datang ke Sekolah', desc: 'Pemasangan & penyelenggaraan dibuat terus di sekolah — anda tidak perlu pening.' },
  { icon: HeartHandshake, title: 'Latihan Sehingga Mahir', desc: 'Guru & staf kaunter dilatih sepenuhnya supaya yakin menggunakan sistem dari hari pertama.' },
  { icon: Layers, title: 'Pindah Data Tanpa Kerumitan', desc: 'Data sedia ada anda kami pindahkan dengan teliti — tiada rekod yang hilang.' },
  { icon: Zap, title: 'Sentiasa Terkini', desc: 'Naik taraf & penambahbaikan setiap tahun, tanpa kos tersembunyi.' },
  { icon: Wrench, title: 'Sokongan Komputer Berterusan', desc: 'Khidmat nasihat & baiki komputer untuk memastikan PSS anda sentiasa lancar.' },
];

// On-field service photos (public/servis/servis-1..14.jpeg)
const SERVIS_PHOTOS = Array.from({ length: 14 }, (_, i) => `/servis/servis-${i + 1}.jpeg`);

// 3D card variants (front/back art). berlogo shown first.
//  - hasQr: berlogo has the big QR (so QR callout applies); bergambar doesn't.
const CARD_VARIANTS = [
  { key: 'berlogo', label: 'Kad Berlogo', front: '/kad/card-front.png', back: '/kad/card-back.png', hasQr: true },
  { key: 'bergambar', label: 'Kad Bergambar', front: '/kad/bergambar-front.png', back: '/kad/bergambar-back.png', hasQr: false },
];

// Real WhatsApp praise from schools & PPD (public/testimoni/t-1..13.jpeg)
const TESTIMONI_PHOTOS = Array.from({ length: 13 }, (_, i) => `/testimoni/t-${i + 1}.jpeg`);

// QR — 3 apps the single QR unlocks (child detail of "1 QR — 3 Kegunaan")
const QR_APPS = [
  {
    logo: '/apps/delima.png',
    name: 'DELIMa',
    desc: 'Digital Educational Learning Initiative Malaysia — log masuk platform pembelajaran KPM tanpa taip.',
  },
  {
    logo: '/apps/ains.png',
    name: 'AINS',
    desc: 'Advanced Integrated NILAM System — sistem bersepadu merekod bahan bacaan NILAM murid.',
  },
  {
    logo: '/zabra-icon.png',
    name: 'Litera PSS',
    desc: '2 sistem: Pinjam-Pulang & Kehadiran PSS — imbas kad, terus rekod.',
    contain: true,
  },
];

// Kelebihan Kad — extra points per benefit (dari PDF), revealed via eye icon.
const KAD_BENEFITS = [
  {
    icon: ShieldCheck,
    t: 'Identiti & Pengesahan',
    s: 'Pengenalan rasmi murid & guru — pantas disahkan.',
    details: [
      'Alat pengenalan murid & guru yang mudah dan cepat.',
      'Guru & staf mudah mengenali dan sahkan status ahli sekolah.',
      'Bukti rasmi status pendidik bagi guru.',
    ],
  },
  {
    icon: Layers,
    t: 'Akses Fasiliti',
    s: 'Perpustakaan, makmal & sistem kehadiran sekolah.',
    details: [
      'Akses perpustakaan — Sistem Pinjam Pulang & Kehadiran PSS.',
      'Akses makmal komputer & fasiliti sekolah lain.',
      'Rekod kehadiran sekolah secara digital.',
    ],
  },
  {
    icon: Sparkles,
    t: 'Diskaun di Luar',
    s: 'RapidKL, pawagam, restoran & kedai buku terpilih.',
    details: [
      'Murid: RapidKL (Bus, BRT, LRT, Monorel, MRT), taman tema, pawagam, restoran & kedai buku terpilih.',
      'Guru: hotel (harga penjawat awam), MYDIN, kedai buku & restoran terpilih.',
      'Bantu jimatkan wang ketika membeli-belah di luar sekolah.',
    ],
  },
  {
    icon: Award,
    t: 'Keselamatan',
    s: 'Kawalan keluar-masuk & pengecaman ketika kecemasan.',
    details: [
      'Pastikan hanya warga sekolah yang sah masuk ke premis.',
      'Pantau pergerakan keluar-masuk untuk tujuan keselamatan.',
      'Bantu mengenal pasti identiti jika berlaku insiden di luar sekolah.',
    ],
  },
];

/* =========================================================================
   CAMERA TRANSITION VARIANTS
   ========================================================================= */
const sceneVariants = {
  enter: (dir) => ({ opacity: 0, scale: dir === 'in' ? 0.4 : 1.6, filter: 'blur(8px)' }),
  center: {
    opacity: 1,
    scale: 1,
    filter: 'blur(0px)',
    transition: { type: 'spring', stiffness: 120, damping: 18, mass: 0.8 },
  },
  exit: (dir) => ({
    opacity: 0,
    scale: dir === 'in' ? 1.6 : 0.4,
    filter: 'blur(8px)',
    transition: { duration: 0.3 },
  }),
};

/* =========================================================================
   PACKAGE CARD — primary CTA, premium "hero" treatment
   ========================================================================= */
const PackageCard = ({ pkg, index, onClick, students, selectedTier, config }) => {
  const t = pkg.theme;
  const Icon = pkg.icon;
  const prices = computePrices(students, selectedTier, config);
  const { price: livePrice, original: liveOriginal, note: liveNote } = packageInitial(pkg, prices, config);
  return (
    <motion.button
      initial={{ opacity: 0, x: -30 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.1 + index * 0.1, type: 'spring', stiffness: 130, damping: 16 }}
      whileHover={{ scale: 1.025, y: -5 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="group relative text-left rounded-3xl p-[1.5px] overflow-hidden"
      style={{
        background: `linear-gradient(140deg, ${t.from}, ${t.to})`,
        boxShadow: pkg.recommended
          ? `0 25px 60px -20px ${t.glow}cc`
          : `0 18px 45px -24px ${t.glow}88`,
      }}
    >
      {/* glowing animated halo for the recommended card */}
      {pkg.recommended && (
        <motion.span
          aria-hidden
          className="pointer-events-none absolute -inset-10 opacity-40"
          style={{ background: `radial-gradient(circle at 30% 0%, ${t.from}, transparent 60%)` }}
          animate={{ opacity: [0.25, 0.5, 0.25] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        />
      )}

      {/* inner surface */}
      <div className="relative rounded-[22px] p-5 bg-white backdrop-blur-xl h-full">
        {/* sweeping shine on hover */}
        <span className="pointer-events-none absolute inset-0 rounded-[22px] overflow-hidden">
          <span
            className="absolute -left-1/3 top-0 h-full w-1/3 -skew-x-12 bg-black/[0.05] opacity-0 group-hover:opacity-100 group-hover:translate-x-[400%] transition-all duration-700 ease-out"
          />
        </span>

        {/* top accent bar */}
        <span
          className="absolute top-0 left-6 right-6 h-1 rounded-b-full"
          style={{ background: `linear-gradient(90deg, ${t.from}, ${t.light})` }}
        />

        {pkg.recommended && (
          <span
            className="absolute top-5 right-5 inline-flex items-center gap-1 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-lg"
            style={{ background: `linear-gradient(90deg, ${t.from}, ${t.to})` }}
          >
            <Star size={10} className="fill-white" /> Disyorkan
          </span>
        )}

        <div className="flex items-center gap-3 mb-2">
          {pkg.logoImg ? (
            <img
              src={pkg.logoImg}
              alt={pkg.name}
              className="w-10 h-10 rounded-xl shrink-0 object-cover"
              style={{ boxShadow: `inset 0 0 0 1px ${t.from}55` }}
            />
          ) : (
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: `${t.from}33`, boxShadow: `inset 0 0 0 1px ${t.from}55` }}
            >
              <Icon size={22} style={{ color: t.light }} />
            </div>
          )}
          <div>
            <h2 className="text-xl font-extrabold text-[#14110d] leading-tight">{pkg.name}</h2>
            <p className="text-sm font-medium" style={{ color: t.light }}>
              {pkg.tagline}
            </p>
          </div>
        </div>

        <p className="text-[13px] text-[#5c554d] mb-3 line-clamp-2">{pkg.diff}</p>

        <div className="flex items-end justify-between gap-3">
          <div>
            {liveOriginal > livePrice && (
              <span className="flex items-center gap-1.5 mb-0.5">
                <span className="text-base font-semibold text-[#5c554d] line-through decoration-red-400 decoration-2">
                  {formatRM(liveOriginal)}
                </span>
                <span className="text-[9px] font-bold text-white bg-red-500 rounded-full px-1.5 py-0.5">
                  JIMAT {Math.round(((liveOriginal - livePrice) / liveOriginal) * 100)}%
                </span>
              </span>
            )}
            <motion.span
              key={livePrice}
              initial={{ opacity: 0.5, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-3xl font-extrabold text-[#14110d] tracking-tight inline-block"
            >
              {formatRM(livePrice)}
            </motion.span>
            <span className="block text-[11px] text-[#5c554d] mt-0.5">{liveNote}</span>
          </div>
          <span
            className="flex items-center gap-1.5 text-white text-base font-bold px-4 py-2.5 rounded-full shadow-md transition-transform group-hover:translate-x-0.5"
            style={{ background: `linear-gradient(90deg, ${t.from}, ${t.to})` }}
          >
            Teroka pakej
            <ChevronRight size={16} className="transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </motion.button>
  );
};

/* =========================================================================
   PRODUCT CARD — top-level product picker (Litera / Digitera)
   ========================================================================= */
const ProductCard = ({ index, logoImg, name, subname, tagline, theme, bullets = [], onClick }) => (
  <motion.button
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.12 + index * 0.08, type: 'spring', stiffness: 130, damping: 16 }}
    onClick={onClick}
    className="group text-left rounded-2xl p-5 border border-black/10 bg-white backdrop-blur-md transition hover:-translate-y-0.5"
    style={{ boxShadow: `0 10px 30px -18px ${theme.from}88` }}
  >
    <div className="flex items-center gap-3 mb-3">
      {logoImg ? (
        <img
          src={logoImg}
          alt={name}
          className="w-12 h-12 rounded-xl shrink-0 object-cover"
          style={{ boxShadow: `inset 0 0 0 1px ${theme.from}55` }}
        />
      ) : (
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: `${theme.from}22` }}
        >
          <Layers size={24} style={{ color: theme.light }} />
        </div>
      )}
      <div className="min-w-0">
        <h2 className="text-xl font-extrabold text-[#14110d] leading-tight">{name}</h2>
        <p className="text-sm font-medium" style={{ color: theme.light }}>
          {subname}
        </p>
      </div>
    </div>
    <p className="text-sm text-[#5c554d] mb-3">{tagline}</p>
    <ul className="space-y-1.5 mb-4">
      {bullets.map((b) => (
        <li key={b} className="flex items-start gap-2">
          <Check size={15} className="mt-0.5 shrink-0" style={{ color: theme.light }} />
          <span className="text-[13px] text-[#3a352e]">{b}</span>
        </li>
      ))}
    </ul>
    <span
      className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-full px-4 py-2 text-white transition"
      style={{ background: theme.from }}
    >
      Lihat produk <ChevronRight size={16} className="group-hover:translate-x-0.5 transition" />
    </span>
  </motion.button>
);

/* =========================================================================
   ROOT DASHBOARD — matches the sketch
   ========================================================================= */
const RootScene = ({ go, students, setStudents, selectedTier, setSelectedTier, config }) => (
  <div className="w-full max-w-6xl mx-auto">
    <div className="text-center mb-3">
      <span className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-[#c4b5fd] text-[11px] px-3 py-1 rounded-full mb-2">
        <Sparkles size={12} /> 13+ tahun · 200+ sekolah
      </span>
      <h1 className="text-2xl md:text-3xl font-extrabold text-white leading-tight">
        Sistem pusat sumber pilihan sekolah terbaik.
      </h1>
      <p className="text-gray-300 text-base mt-1">
         Digital, Mesra, Pantas &amp; <span className="text-white font-semibold">Tanpa Kontrak</span>.
      </p>
    </div>

    {/* compact student/category pill */}
    <div className="flex justify-center mb-4">
      <StudentBar
        students={students}
        setStudents={setStudents}
        selectedTier={selectedTier}
        setSelectedTier={setSelectedTier}
      />
    </div>

    <div className="space-y-4">
      {/* PRODUK — dua produk setara: Litera PSS & Digitera */}
      <p className="text-sm uppercase tracking-wider text-[#c4b5fd] font-bold mb-1 px-1">
        Pilih produk
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <ProductCard
          index={0}
          logoImg="/litera-icon.png"
          name="Litera PSS"
          subname="Sistem Pusat Sumber Digital"
          tagline="Automasi PSS — pinjam-pulang, kehadiran & laporan"
          theme={{ from: '#7c3aed', light: '#7c3aed' }}
          bullets={['Pakej Sistem (beli terus)', 'Pakej Buku (melalui PCG)', 'Patuh rubrik PSS KPM']}
          onClick={() => go(['litera'], 'in')}
        />
        <ProductCard
          index={1}
          logoImg={PACKAGES.digitera.logoImg}
          name="Digitera"
          subname="Sistem Kawalan Sekolah"
          tagline="Kawalan kehadiran guru & staf"
          theme={{ from: '#0d9488', light: '#0d9488' }}
          bullets={['Kiosk pengganti punch card', 'Akses mobile setiap guru', 'Analisa lengkap untuk pentadbir']}
          onClick={() => go(['pkg', PACKAGES.digitera.id], 'in')}
        />
        <ProductCard
          index={2}
          logoImg={PACKAGES.ezbook.logoImg}
          name="EzBook"
          subname="Sistem Tempahan Bilik"
          tagline="Tempahan bilik & fasiliti — mudah & mesra mobile"
          theme={{ from: '#059669', light: '#059669' }}
          bullets={['Admin setup mudah', 'Kolaborator urus tempahan', 'Orang awam lihat & mohon']}
          onClick={() => go(['pkg', PACKAGES.ezbook.id], 'in')}
        />
      </div>

      {/* KAD ZABRA — add-on (bukan produk berdiri sendiri) */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, type: 'spring', stiffness: 130, damping: 16 }}
        className="rounded-2xl p-5 border border-black/10 bg-white backdrop-blur-md"
      >
        <p className="text-sm uppercase tracking-wider text-[#6d28d9] font-bold mb-3">
          Add-on · Optimumkan penggunaan sistem
        </p>
        <PanelNode
          icon={CreditCard}
          label="Kad Zabra"
          sub="Untuk aktiviti lebih lancar"
          onClick={() => go(['kad'], 'in')}
          wide
        />
      </motion.div>
    </div>

    {/* trust-led reassurance strip */}
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.45 }}
      className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm"
    >
      <span className="flex items-center gap-1.5 text-gray-200">
        <ShieldCheck size={14} className="text-[#a78bfa]" /> Tanpa kontrak — tiada ikatan
      </span>
      <span className="flex items-center gap-1.5 text-gray-200">
        <Award size={14} className="text-[#a78bfa]" /> Patuh rubrik PSS KPM
      </span>
      <span className="flex items-center gap-1.5 text-gray-200">
        <HeartHandshake size={14} className="text-[#a78bfa]" /> Boleh dibiayai PCG / PIBG
      </span>
    </motion.div>
    <p className="text-center text-[11px] text-gray-300 mt-3">
      Klik mana-mana kad untuk teroka butirannya.
    </p>
  </div>
);

/* Placeholder scene for sections not yet populated (e.g. Digitera servis). */
const EmptyScene = ({ title, note }) => (
  <SubScene title={title}>
    <div className="rounded-2xl border border-black/10 bg-white p-10 text-center">
      <div className="mx-auto mb-3 w-12 h-12 rounded-xl flex items-center justify-center bg-black/[0.04]">
        <Sparkles size={22} className="text-[#0d9488]" />
      </div>
      <p className="text-[#14110d] font-semibold text-lg">{note}</p>
      <p className="text-sm text-[#5c554d] mt-1">Nantikan kemas kini seterusnya.</p>
    </div>
  </SubScene>
);

/* =========================================================================
   DIGITERA — pain points (Dulu → Sekarang)
   ========================================================================= */
const DIGITERA_PAINS = [
  {
    title: 'Analisa & laporan lambat dan tidak tepat',
    dulu: ['Perlukan kerani untuk pengiraan manual waktu masuk & keluar setiap hari.'],
    sekarang: [
      'Hanya export Excel — data lengkap terus dari sistem:',
      'Waktu masuk (awal atau lewat)',
      'Waktu keluar',
      'Jumlah cuti & siapa',
      'Rekod tugas rasmi terperinci',
      'Simpan hardcopy — boleh print & fail sendiri untuk pemeriksaan pegawai',
    ],
  },
  {
    title: 'Cara pengrekodan kehadiran terhad',
    dulu: [
      'Menggunakan punch card:',
      'Perlu restok kad',
      'Perlu tunggu pejabat buka',
      'Perlu pengiraan manual setiap staf',
    ],
    sekarang: [
      'Guna telefon pintar masing-masing:',
      'Dalam kawasan sekolah — clock in, out & pelepasan',
      'Luar kawasan — mohon cuti, tugas rasmi & lihat rekod sendiri',
      'Alternatif: scan kad matrik guru di pejabat',
      '2 kaedah, 1 sumber data tepat — sistem halang rekod berganda',
    ],
  },
];

const BeforeAfterCard = ({ title, dulu, sekarang, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.08 }}
    className="rounded-2xl border border-black/10 bg-white overflow-hidden"
  >
    <div className="px-5 py-3 border-b border-black/10">
      <span className="text-[11px] font-bold uppercase tracking-wider text-[#0d9488]">
        Cabaran #{index + 1}
      </span>
      <h4 className="text-[#14110d] font-bold text-lg leading-tight">{title}</h4>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2">
      {/* DULU */}
      <div className="p-5 bg-red-50/70 border-b sm:border-b-0 sm:border-r border-black/10">
        <p className="text-[11px] font-bold uppercase tracking-wider text-red-500 mb-2">Dulu</p>
        <ul className="space-y-1.5">
          {dulu.map((d, i) => (
            <li key={i} className="flex items-start gap-2 text-[13px] text-[#3a352e]">
              <X size={14} className="mt-0.5 shrink-0 text-red-400" />
              <span>{d}</span>
            </li>
          ))}
        </ul>
      </div>
      {/* SEKARANG */}
      <div className="p-5 bg-emerald-50/70">
        <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-600 mb-2">Sekarang</p>
        <ul className="space-y-1.5">
          {sekarang.map((s, i) => (
            <li key={i} className="flex items-start gap-2 text-[13px] text-[#3a352e]">
              <Check size={14} className="mt-0.5 shrink-0 text-emerald-500" />
              <span>{s}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </motion.div>
);

const KenapaDigiteraScene = () => (
  <SubScene title="Kenapa Digitera?" subtitle="Dari cara lama yang menyusahkan, ke cara digital yang menyelesaikan">
    <div className="space-y-5">
      {DIGITERA_PAINS.map((p, i) => (
        <BeforeAfterCard key={p.title} index={i} {...p} />
      ))}
    </div>
  </SubScene>
);

/* =========================================================================
   SHARED — Kenapa Percaya Zabra (company-wide trust)
   ========================================================================= */
const TRUST_POINTS = [
  {
    icon: Award,
    title: 'Bukan Sistem Baru Nak Mula',
    body:
      'Kami bukan baru nak start. Lebih 13 tahun bertapak dalam sistem digital sekolah — kami turun ke sekolah, dengar suara guru & pentadbir, ke arah sekolah digital yang menyelesaikan masalah, bukan menambah masalah.',
  },
  {
    icon: ShieldCheck,
    title: 'Sudah Terbukti di Sekolah',
    body:
      'Digitera telah beroperasi hampir setahun di sekolah apprentice terpilih seperti SK Taman Seroja & SMK Taman Medan. Mereka sangat berpuas hati — malah pelbagai penambahbaikan dari mereka telah kami laksanakan.',
  },
  {
    icon: Lock,
    title: 'Penjagaan Data Diamanahkan',
    body:
      'Lebih 13 tahun bersemuka dengan pelbagai sekolah — tiada satu pun data pecah amanah dari kami. Atas inisiatif sendiri, kami keluarkan surat amanah penjagaan data. Sekolah juga disarankan agar tidak sewenang-wenangnya kongsi data murid/guru dengan agensi yang tidak dikenali.',
  },
  {
    icon: HeartHandshake,
    title: 'Syarikat Tempatan, Pasukan Inhouse',
    body:
      'Zabra ialah syarikat tempatan. Talent kami — programmer & data analyst — semuanya dari dalam syarikat sendiri. Bukan outsource, bukan pihak ketiga.',
  },
];

const KenapaPercayaScene = () => (
  <SubScene title="Kenapa Percaya Zabra?" subtitle="13+ tahun, data diamanahkan, pasukan tempatan">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {TRUST_POINTS.map((t, i) => {
        const Icon = t.icon;
        return (
          <motion.div
            key={t.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08 }}
            className="rounded-2xl border border-black/10 bg-white p-5"
          >
            <div className="flex items-center gap-3 mb-2">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: `${ACCENT}22` }}
              >
                <Icon size={20} style={{ color: ACCENT_LIGHT }} />
              </div>
              <h4 className="text-[#14110d] font-bold text-base leading-tight">{t.title}</h4>
            </div>
            <p className="text-[13px] text-[#3a352e] leading-relaxed">{t.body}</p>
          </motion.div>
        );
      })}
    </div>
  </SubScene>
);

/* =========================================================================
   LITERA — product sub-hub: two packages + why-Litera panels
   ========================================================================= */
const LiteraScene = ({ go, students, setStudents, selectedTier, setSelectedTier, config }) => (
  <div className="w-full max-w-5xl mx-auto">
    <div className="text-center mb-2">
      <h3 className="text-2xl md:text-4xl font-extrabold text-white">
        <span style={{ color: '#a78bfa' }}>·</span> Litera PSS{' '}
        <span style={{ color: '#a78bfa' }}>·</span>
      </h3>
      <p className="mt-2 text-sm md:text-base font-medium tracking-wide text-white/70">
        Sistem Pusat Sumber Digital
      </p>
    </div>

    <div className="flex justify-center my-4">
      <StudentBar
        students={students}
        setStudents={setStudents}
        selectedTier={selectedTier}
        setSelectedTier={setSelectedTier}
      />
    </div>

    <p className="text-sm uppercase tracking-wider text-[#c4b5fd] font-bold mb-2 px-1">Pilih pakej</p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {[PACKAGES.sistem, PACKAGES.buku].map((pkg, i) => (
        <PackageCard
          key={pkg.id}
          pkg={pkg}
          index={i}
          students={students}
          selectedTier={selectedTier}
          config={config}
          onClick={() => go(['litera', 'pkg', pkg.id], 'in')}
        />
      ))}
    </div>

    {/* Kenapa Pilih Litera — kelebihan / servis / testimoni (khusus Litera) */}
    <div className="mt-4 rounded-2xl p-5 border border-black/10 bg-white backdrop-blur-md">
      <p className="text-sm uppercase tracking-wider text-[#6d28d9] font-bold mb-3">
        Kenapa Pilih Litera?
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <PanelNode icon={Sparkles} label="Kelebihan Litera PSS" sub="Paparan sebenar" onClick={() => go(['litera', 'kelebihan'], 'in')} />
        <PanelNode icon={ShieldCheck} label="Kenapa Percaya Zabra?" sub="13+ tahun · data amanah" onClick={() => go(['litera', 'percaya'], 'in')} />
        <PanelNode icon={Wrench} label="Servis Zabra" onClick={() => go(['litera', 'servis'], 'in')} />
        <PanelNode icon={Quote} label="Kata Mereka" sub="Testimoni sekolah" onClick={() => go(['litera', 'testimoni'], 'in')} />
      </div>
    </div>

    <p className="text-center text-[11px] text-gray-300 mt-4">
      Klik mana-mana kad untuk teroka butirannya.
    </p>
  </div>
);

/* a clickable pill-node inside the shared panels */
const PanelNode = ({ icon: Icon, label, sub, onClick, wide }) => (
  <motion.button
    whileHover={{ scale: 1.04, y: -3 }}
    whileTap={{ scale: 0.97 }}
    onClick={onClick}
    className={`group flex items-center gap-3 rounded-xl px-4 py-4 border transition text-left ${
      wide ? 'w-full' : 'w-full'
    }`}
    style={{
      background: `linear-gradient(150deg, ${ACCENT}33, rgba(255,255,255,0.03))`,
      borderColor: `${ACCENT}66`,
    }}
  >
    <div
      className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
      style={{ background: `${ACCENT}44` }}
    >
      <Icon size={20} style={{ color: ACCENT_LIGHT }} />
    </div>
    <div className="min-w-0">
      <span className="block font-bold text-[#14110d] text-base">{label}</span>
      {sub && <span className="block text-sm text-[#5c554d]">{sub}</span>}
    </div>
    <ChevronRight
      size={16}
      className="ml-auto text-[#14110d]/50 group-hover:text-[#14110d] transition shrink-0"
    />
  </motion.button>
);

/* =========================================================================
   STUDENT BAR — persistent input shown on top of every package scene
   ========================================================================= */
const StudentBar = ({ students, setStudents, selectedTier, setSelectedTier }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);
  const s = safeStudents(students);
  const autoTi = tierIndexFor(s);
  const activeTi = selectedTier == null ? autoTi : selectedTier;
  const showAll = selectedTier == null;
  const clampedValue = Math.min(Math.max(s, 0), MAX_STUDENTS);

  // Position the portal panel under the trigger (escapes overflow-clipping ancestors).
  useLayoutEffect(() => {
    if (open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setMenuPos({ top: r.bottom + 8, left: r.left, width: r.width });
    }
  }, [open]);

  const summary = `${showAll ? TIERS[autoTi].label : TIERS[activeTi].label} · ${s.toLocaleString(
    'en-MY'
  )} murid`;

  return (
    <div className="inline-block">
      {/* COMPACT TRIGGER PILL — shows summary; opens the editor on click */}
      <button
        ref={btnRef}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 bg-white hover:bg-[#f5f3ff] border border-black/15 shadow-sm rounded-full pl-2 pr-3 py-1.5 transition"
        title="Tukar kategori & bilangan murid"
      >
        <span
          className="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
          style={{ background: `${ACCENT}` }}
        >
          <Users size={13} className="text-white" />
        </span>
        <span className="text-base font-semibold text-[#14110d]">{summary}</span>
        <ChevronDown
          size={14}
          className={`text-[#14110d]/70 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open &&
        menuPos &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[90]" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              style={{ position: 'fixed', top: menuPos.top, left: menuPos.left }}
              className="z-[100] w-80 max-w-[92vw] bg-white border border-black/10 rounded-2xl p-4 shadow-xl"
            >
              {/* Category */}
              <p className="text-[11px] font-bold uppercase tracking-wide text-[#c4b5fd] mb-2">
                Kategori sekolah
              </p>
              <div className="grid grid-cols-2 gap-1.5 mb-4">
                {TIERS.map((tr, i) => idx(i))
                  .reverse()
                  .map((i) => {
                    const tr = TIERS[i];
                    const on = selectedTier === i;
                    return (
                      <button
                        key={tr.key}
                        onClick={() => setSelectedTier(i)}
                        className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border text-left transition"
                        style={
                          on
                            ? { background: tr.color, borderColor: tr.color, color: i <= 1 ? '#14532d' : '#fff' }
                            : { background: `${tr.color}22`, borderColor: `${tr.color}88`, color: '#3a352e' }
                        }
                      >
                        {tr.label}
                        <span className="block text-[9px] opacity-80 font-normal">{tr.range}</span>
                      </button>
                    );
                  })}
                <button
                  onClick={() => setSelectedTier(null)}
                  className={`col-span-2 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border transition ${
                    showAll ? 'bg-[#7c3aed] text-white border-[#7c3aed]' : 'bg-white border-black/15 text-[#3a352e]'
                  }`}
                >
                  Lihat Semua (auto ikut murid)
                </button>
              </div>

              {/* Murid count */}
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[11px] font-bold uppercase tracking-wide text-[#c4b5fd]">
                  Bilangan murid
                </p>
                <div className="flex items-center gap-1.5">
                  <input
                    type="number"
                    min={0}
                    max={MAX_STUDENTS}
                    value={Number.isFinite(Number(students)) ? students : 0}
                    onChange={(e) =>
                      setStudents(Math.max(0, Math.min(MAX_STUDENTS, safeStudents(e.target.value))))
                    }
                    className="w-20 px-2 py-1 rounded-lg bg-black/[0.05] text-[#14110d] text-right font-bold text-base outline-none border border-black/10 focus:border-[#7c3aed]"
                  />
                  <span className="text-[#5c554d] text-sm">murid</span>
                </div>
              </div>

              {/* Slider */}
              <div className="relative h-5 flex items-center">
                {showAll ? (
                  <>
                    <div className="absolute inset-x-0 h-2 rounded-full overflow-hidden flex">
                      {TIERS.map((tr, i) => {
                        const lo = i === 0 ? 0 : TIER_BREAKS[i - 1];
                        const hi = i < TIER_BREAKS.length ? TIER_BREAKS[i] : MAX_STUDENTS;
                        return (
                          <div
                            key={tr.key}
                            style={{ width: `${((hi - lo) / MAX_STUDENTS) * 100}%`, background: tr.color }}
                          />
                        );
                      })}
                    </div>
                    {TIER_BREAKS.map((bp) => (
                      <div
                        key={bp}
                        className="absolute top-1/2 -translate-y-1/2 w-px h-3 bg-black/30"
                        style={{ left: `${(bp / MAX_STUDENTS) * 100}%` }}
                      />
                    ))}
                  </>
                ) : (
                  <div
                    className="absolute inset-x-0 h-2 rounded-full"
                    style={{ background: TIERS[activeTi].color }}
                  />
                )}
                <input
                  type="range"
                  min={0}
                  max={MAX_STUDENTS}
                  step={25}
                  value={clampedValue}
                  onChange={(e) => setStudents(Number(e.target.value))}
                  className="tier-slider relative w-full cursor-pointer bg-transparent appearance-none z-10"
                  style={{ '--thumb': showAll ? TIERS[autoTi].color : TIERS[activeTi].color }}
                />
              </div>
              <div className="flex justify-between text-[9px] text-[#5c554d] mt-1">
                <span>0</span>
                <span>{MAX_STUDENTS.toLocaleString('en-MY')}</span>
              </div>

              <button
                onClick={() => setOpen(false)}
                className="w-full mt-3 bg-[#7c3aed] hover:bg-[#6d28d9] text-white text-base font-semibold py-2 rounded-lg transition"
              >
                Selesai
              </button>
            </motion.div>
          </>,
          document.body
        )}
    </div>
  );
};

// small helper so we can .reverse() a fresh index array without lint noise
const idx = (i) => i;

/* A single price tile (used for initial-year & next-year).
   `original` (optional) shows a crossed-out anchor price + a savings badge. */
const PriceTile = ({ label, value, note, badge, highlight, accent, original }) => {
  const showOriginal = Boolean(original) && original > value;
  const savePct = showOriginal ? Math.round(((original - value) / original) * 100) : 0;
  return (
    <div
      className="rounded-xl p-5 border-2 bg-white"
      style={highlight ? { borderColor: accent, boxShadow: `0 6px 20px -10px ${accent}66` } : { borderColor: 'rgba(0,0,0,0.1)' }}
    >
      <p className="text-sm text-[#5c554d] mb-1">{label}</p>
      {showOriginal && (
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-lg font-semibold text-[#5c554d] line-through decoration-red-400 decoration-2">
            {formatRM(original)}
          </span>
          <span className="text-[10px] font-bold text-white bg-red-500 rounded-full px-2 py-0.5">
            JIMAT {savePct}%
          </span>
        </div>
      )}
      <motion.div
        key={value}
        initial={{ scale: 0.92, opacity: 0.5 }}
        animate={{ scale: 1, opacity: 1 }}
        className="text-3xl font-extrabold text-[#14110d]"
      >
        {formatRM(value)}
      </motion.div>
      {note && <p className="text-[11px] text-[#5c554d] mt-1">{note}</p>}

    <AnimatePresence>
      {badge && (
        <motion.p
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="mt-2 text-[11px] font-semibold text-[#6d28d9] bg-[#7c3aed]/15 rounded-lg px-2.5 py-1.5"
        >
          {badge}
        </motion.p>
      )}
    </AnimatePresence>
    </div>
  );
};

/* =========================================================================
   SUB-SCENES
   ========================================================================= */
const SubScene = ({ title, subtitle, children }) => (
  <div className="flex flex-col items-center justify-center h-full w-full">
    <motion.h3
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-2xl md:text-4xl font-extrabold text-white text-center"
    >
      <span style={{ color: '#a78bfa' }}>·</span> {title}{' '}
      <span style={{ color: '#a78bfa' }}>·</span>
    </motion.h3>
    {subtitle && (
      <motion.p
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="mt-2 text-sm md:text-base font-medium tracking-wide text-white/70 text-center"
      >
        {subtitle}
      </motion.p>
    )}
    <div className="w-full max-w-4xl px-2 mt-8">{children}</div>
  </div>
);

// Package detail — single surface: murid bar on top, both prices live, includes below.
const PackageScene = ({ pkg, students, setStudents, selectedTier, setSelectedTier, config, onPanel }) => {
  const p = computePrices(students, selectedTier, config);
  const t = pkg.theme;

  const TIER_KEYS = {
    sistem: 'sistemInitialTiers',
    buku: 'bukuInitialTiers',
    digitera: 'digiteraInitialTiers',
  };
  const initialTiers = config[TIER_KEYS[pkg.id]] || config.bukuInitialTiers;

  const promoOnBuku = pkg.id === 'buku' && p.promoActive;
  const isSolo = isSoloProduct(pkg);
  const { price: initial, original: initialOriginal } = packageInitial(pkg, p, config);
  const initialNote =
    pkg.id === 'sistem'
      ? `harga ${p.tier.label.toLowerCase()}`
      : promoOnBuku
      ? `${p.promoName} · sistem PERCUMA`
      : isSolo
      ? `harga ${p.tier.label.toLowerCase()}`
      : `harga ${p.tier.label.toLowerCase()} · sistem PERCUMA`;

  // Renewal = Premium subscription by default. Basic is a hidden fallback,
  // revealed only when the school can't commit.
  const [method, setMethod] = useState('murid'); // 'murid' | 'buku'
  const [showBasic, setShowBasic] = useState(false);
  const [showSubs, setShowSubs] = useState(false); // reveal 'tahun seterusnya' block

  const muridValue = p.muridPremium;
  const bukuValue = p.bukuPremium;
  const soloRenew = soloRenewalValue(pkg, p);

  return (
    <SubScene title={pkg.name} subtitle={pkg.subname}>
      <div className="flex justify-center mb-4">
        <StudentBar
          students={students}
          setStudents={setStudents}
          selectedTier={selectedTier}
          setSelectedTier={setSelectedTier}
        />
      </div>

      {/* Tahun pertama — akaun Premium disertakan */}
      <div className="mb-3">
        <PriceTile
          label="Tahun pertama · Akaun Premium disertakan"
          value={initial}
          original={initialOriginal}
          note={initialNote}
          highlight
          accent={t.from}
        />
      </div>

      {/* tier breakdown — only shown in "Lihat Semua" mode */}
      {p.autoTier && (
        <div className="mb-5">
          <p className="text-center text-[11px] text-gray-300 mb-2">
            Harga tahun pertama mengikut kategori sekolah (tekan untuk pilih):
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {TIERS.map((tr, i) => {
              const active = i === p.tierIndex;
              const lightTier = i <= 1; // teal & light green need dark text
              return (
                <button
                  key={tr.key}
                  onClick={() => setSelectedTier(i)}
                  className="rounded-xl px-3 py-2.5 text-center border transition hover:brightness-110"
                  style={
                    active
                      ? {
                          background: tr.color,
                          borderColor: tr.color,
                          color: lightTier ? '#14532d' : '#ffffff',
                        }
                      : { background: `${tr.color}1f`, borderColor: `${tr.color}55` }
                  }
                >
                  <p className="text-[10px] uppercase tracking-wide mb-0.5 opacity-90">{tr.label}</p>
                  <p className="font-extrabold" style={active ? {} : { color: tr.color }}>
                    {formatRM(initialTiers[i])}
                  </p>
                  <p className="text-[10px] mt-0.5 opacity-80">{tr.range}</p>
                  {active && (
                    <span className="mt-1 inline-block text-[9px] font-bold px-2 py-0.5 rounded-full bg-black/25">
                      SEKOLAH ANDA
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* apa yang sekolah dapat — dikumpulkan mengikut kategori */}
      <div className="mb-2">
        <p className="text-white font-semibold text-base mb-2 px-1">Apa yang sekolah dapat:</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {pkg.groups.map((grp, gi) => (
            <motion.div
              key={grp.title}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: gi * 0.06 }}
              className="bg-white border border-black/10 rounded-xl p-4"
            >
              <p
                className="text-[11px] font-bold uppercase tracking-wide mb-2"
                style={{ color: t.light }}
              >
                {/^Buku Bernilai/i.test(grp.title)
                  ? `Buku Bernilai RM${Number(p.bukuInitial).toLocaleString('en-MY')}`
                  : grp.title}
              </p>
              <ul className="space-y-1.5">
                {grp.items.map((it, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check size={15} className="mt-0.5 shrink-0" style={{ color: t.light }} />
                    <span className="text-[13px] text-[#3a352e]">{applyItemConfig(it, config)}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Kenapa Pilih <produk> — kelebihan / kenapa / percaya / servis / testimoni */}
      {onPanel && (
        <div className="mt-4 rounded-2xl p-5 border border-black/10 bg-white">
          <p className="text-sm uppercase tracking-wider font-bold mb-3" style={{ color: t.from }}>
            Kenapa Pilih {pkg.name}?
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {pkg.id === 'digitera' && (
              <PanelNode icon={Zap} label="Kenapa Digitera?" sub="Dulu vs Sekarang" onClick={() => onPanel('kenapa')} />
            )}
            <PanelNode icon={Sparkles} label={`Kelebihan ${pkg.name}`} sub="Paparan sebenar" onClick={() => onPanel('kelebihan')} />
            <PanelNode icon={ShieldCheck} label="Kenapa Percaya Zabra?" sub="13+ tahun · data amanah" onClick={() => onPanel('percaya')} />
            <PanelNode icon={Wrench} label="Servis Zabra" onClick={() => onPanel('servis')} />
            <PanelNode icon={Quote} label="Kata Mereka" sub="Testimoni sekolah" onClick={() => onPanel('testimoni')} />
          </div>
        </div>
      )}

      {/* NO KONTRAK reassurance — selepas 'apa sekolah dapat', sebelum subscription */}
      <div className="flex flex-col items-center text-center gap-1.5 mt-6 mb-2">
        <span
          className="inline-flex items-center gap-2 text-white text-base font-bold px-4 py-2 rounded-full"
          style={{ background: `linear-gradient(90deg, ${t.from}, ${t.to})` }}
        >
          <ShieldCheck size={16} /> NO KONTRAK
        </span>
        <p className="text-sm text-gray-300 max-w-md">
          Bayar untuk tahun seterusnya <span className="text-white font-semibold">hanya jika</span>{' '}
          anda berpuas hati dengan sistem kami. Tiada ikatan, tiada paksaan.
        </p>
        <p className="inline-flex items-center gap-1.5 mt-1 text-[11px] text-[#6d28d9] bg-white border border-black/10 rounded-full px-3 py-1">
          <HeartHandshake size={12} /> Boleh dibiayai melalui peruntukan PCG / PIBG atau kedua-duanya.
        </p>
      </div>

      {/* ===== TAHUN SETERUSNYA — di bawah, tersembunyi di sebalik butang ===== */}
      <div className="mt-6 pt-5 border-t border-black/10">
        {!showSubs ? (
          <div className="text-center">
            <button
              onClick={() => setShowSubs(true)}
              className="inline-flex items-center gap-2 text-white font-semibold py-3 px-6 rounded-full transition"
              style={{ background: `linear-gradient(90deg, ${t.from}, ${t.to})` }}
            >
              <Sparkles size={16} /> Lihat Pakej Subscription
              <ChevronDown size={16} />
            </button>
            <p className="text-[11px] text-gray-300 mt-2">
              Pembaharuan tahun seterusnya — tanpa kontrak.
            </p>
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center justify-between mb-2 px-1">
              <p className="text-white font-semibold text-base flex items-center gap-1.5">
                <Sparkles size={14} style={{ color: t.light }} /> Langganan PREMIUM — tahun seterusnya
              </p>
              <button
                onClick={() => setShowSubs(false)}
                className="text-[11px] text-gray-300 hover:text-white underline underline-offset-2"
              >
                simpan
              </button>
            </div>

            {/* METHOD: per murid vs beli buku (Premium prices).
                Solo products (Digitera/EzBook) have a single annual subscription. */}
            <div className={`grid grid-cols-1 ${isSolo ? '' : 'sm:grid-cols-2'} gap-3`}>
              {(isSolo
                ? [{ key: 'murid', label: 'Langganan tahunan', value: soloRenew, icon: pkg.icon }]
                : [
                    {
                      key: 'murid',
                      label: p.renewalMode === 'fixed' ? 'Kadar tetap tahunan' : 'Bayar per murid',
                      value: muridValue,
                      icon: Users,
                    },
                    { key: 'buku', label: 'Beli buku', value: bukuValue, icon: BookOpen },
                  ]
              ).map((opt) => {
                const Icon = opt.icon;
                const selected = method === opt.key;
                const cheaper = muridValue <= bukuValue ? 'murid' : 'buku';
                return (
                  <button
                    key={opt.key}
                    onClick={() => setMethod(opt.key)}
                    className="relative text-left rounded-xl p-5 border transition bg-white"
                    style={
                      selected
                        ? { borderColor: t.from, boxShadow: `0 0 0 1.5px ${t.from}, 0 6px 18px -10px ${t.from}66` }
                        : { borderColor: 'rgba(0,0,0,0.1)' }
                    }
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="flex items-center gap-2 text-sm text-[#5c554d]">
                        <Icon size={15} style={{ color: t.light }} /> {opt.label}
                      </span>
                      {!isSolo && cheaper === opt.key && opt.value > 0 && (
                        <span className="text-[9px] font-bold px-2 py-0.5 rounded-full text-[#14110d]" style={{ background: TIER_COLORS[2] }}>
                          LEBIH JIMAT
                        </span>
                      )}
                    </div>
                    {opt.key === 'murid' ? (
                      isSolo ? (
                        <>
                          {/* Solo product — single fixed annual subscription by tier. */}
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[9px] font-bold text-white rounded-full px-2 py-0.5" style={{ background: t.from }}>
                              KADAR TETAP
                            </span>
                          </div>
                          <motion.div
                            key={soloRenew}
                            initial={{ scale: 0.92, opacity: 0.5 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="text-4xl font-extrabold text-[#14110d] leading-none"
                          >
                            {soloRenew === 0 ? 'PERCUMA' : `RM${soloRenew.toLocaleString('en-MY')}`}
                            {soloRenew > 0 && (
                              <span className="text-lg font-semibold text-[#5c554d]"> / tahun</span>
                            )}
                          </motion.div>
                          <p className="text-[11px] text-[#5c554d] mt-1.5 leading-snug">
                            Langganan tahunan · {p.tier.label}
                          </p>
                        </>
                      ) : p.renewalMode === 'fixed' ? (
                        <>
                          {/* Fixed annual rate by tier — no per-murid math. */}
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[9px] font-bold text-white bg-[#7c3aed] rounded-full px-2 py-0.5">
                              KADAR TETAP
                            </span>
                          </div>
                          <motion.div
                            key={muridValue}
                            initial={{ scale: 0.92, opacity: 0.5 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="text-4xl font-extrabold text-[#14110d] leading-none"
                          >
                            {muridValue === 0 ? 'PERCUMA' : `RM${muridValue.toLocaleString('en-MY')}`}
                            {muridValue > 0 && (
                              <span className="text-lg font-semibold text-[#5c554d]"> / tahun</span>
                            )}
                          </motion.div>
                          <p className="text-[11px] text-[#5c554d] mt-1.5 leading-snug">
                            Kadar tetap tahunan · {p.tier.label}
                          </p>
                        </>
                      ) : p.sistemRenewalFloored ? (
                        <>
                          {/* Small school: per-murid rate falls below the minimum,
                              so a flat floor (e.g. RM200) applies instead. Show that
                              as the hero so it isn't mistaken for RM1.20 × murid. */}
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[9px] font-bold text-white bg-[#7c3aed] rounded-full px-2 py-0.5">
                              KADAR MINIMUM
                            </span>
                          </div>
                          <motion.div
                            key={muridValue}
                            initial={{ scale: 0.92, opacity: 0.5 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="text-4xl font-extrabold text-[#14110d] leading-none"
                          >
                            RM{muridValue.toLocaleString('en-MY')}
                            <span className="text-lg font-semibold text-[#5c554d]"> / tahun</span>
                          </motion.div>
                          <p className="text-[11px] text-[#5c554d] mt-1.5 leading-snug">
                            Kadar minimum servis (RM{config.sistemRenewalMin.toLocaleString('en-MY')})
                            bagi sekolah kecil
                          </p>
                        </>
                      ) : (
                      <>
                        {/* per-murid rate is the hero: crossed RM3 → big RM1.20 */}
                        <div className="flex items-center gap-2 mb-1">
                          {config.renewalPerStudentOriginal > config.sistemRenewalPerStudent && (
                            <>
                              <span className="text-xl font-bold text-[#5c554d] line-through decoration-red-400 decoration-2">
                                RM{config.renewalPerStudentOriginal}
                              </span>
                              <span className="text-[9px] font-bold text-white bg-red-500 rounded-full px-2 py-0.5">
                                JIMAT{' '}
                                {Math.round(
                                  ((config.renewalPerStudentOriginal - config.sistemRenewalPerStudent) /
                                    config.renewalPerStudentOriginal) *
                                    100
                                )}
                                %
                              </span>
                            </>
                          )}
                        </div>
                        <motion.div
                          key={config.sistemRenewalPerStudent}
                          initial={{ scale: 0.92, opacity: 0.5 }}
                          animate={{ scale: 1, opacity: 1 }}
                          className="text-4xl font-extrabold text-[#14110d] leading-none"
                        >
                          RM{config.sistemRenewalPerStudent}
                          <span className="text-lg font-semibold text-[#5c554d]"> / murid aktif</span>
                        </motion.div>
                        <p className="text-[11px] text-[#5c554d] mt-1.5">
                          Premium · hanya ahli aktif dikira
                        </p>
                      </>
                      )
                    ) : (
                      <>
                        <motion.div
                          key={opt.value}
                          initial={{ scale: 0.92, opacity: 0.5 }}
                          animate={{ scale: 1, opacity: 1 }}
                          className="text-3xl font-extrabold text-[#14110d]"
                        >
                          {opt.value === 0 ? 'PERCUMA' : formatRM(opt.value)}
                          {opt.value > 0 && (
                            <span className="text-base font-normal text-[#5c554d]"> / tahun</span>
                          )}
                        </motion.div>
                        <p className="text-[11px] text-[#5c554d] mt-1">Tambah koleksi PSS</p>
                      </>
                    )}
                    <span
                      className="absolute top-3 right-3 w-5 h-5 rounded-full flex items-center justify-center"
                      style={{
                        background: selected ? t.from : 'transparent',
                        border: selected ? 'none' : '1.5px solid rgba(0,0,0,0.25)',
                      }}
                    >
                      {selected && <Check size={12} className="text-white" />}
                    </span>
                  </button>
                );
              })}
            </div>

            

            {/* HIDDEN BASIC FALLBACK — reveal only when school can't commit */}
            <div className="mt-3 text-center">
              {!showBasic ? (
                <button
                  onClick={() => setShowBasic(true)}
                  className="text-[11px] text-gray-300 hover:text-white underline underline-offset-2 transition"
                >
                  Tak boleh commit?
                </button>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-xl border border-black/10 bg-white p-4 text-left"
                >
                  <div className="flex items-start gap-2 mb-2">
                    <ShieldCheck size={16} className="mt-0.5 shrink-0" style={{ color: t.light }} />
                    <p className="text-[12px] text-[#3a352e] leading-snug">
                      Faham — kami tetap mahu bantu sekolah anda. Jadi ini pelan{' '}
                      <span className="font-bold text-[#14110d]">Basic</span>
                      {p.isRahmahEksklusif ? ' (PERCUMA untuk Rahmah Eksklusif)' : ' (separuh harga)'}:
                      ciri teras sahaja — pinjam-pulang &amp; carian, tanpa laporan lanjutan, kad
                      matrik atau naik taraf.
                    </p>
                  </div>
                  <div className={`grid ${isSolo ? 'grid-cols-1' : 'grid-cols-2'} gap-3`}>
                    {isSolo ? (
                      <div className="rounded-lg bg-white border border-black/10 px-3 py-2.5">
                        <p className="text-[10px] text-[#5c554d] mb-0.5">Basic · langganan tahunan</p>
                        <p className="text-xl font-extrabold text-[#14110d]">
                          {soloRenewalBasic(pkg, p) === 0 ? 'PERCUMA' : `${formatRM(soloRenewalBasic(pkg, p))}`}
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="rounded-lg bg-white border border-black/10 px-3 py-2.5">
                          <p className="text-[10px] text-[#5c554d] mb-0.5">Basic · per murid</p>
                          <p className="text-xl font-extrabold text-[#14110d]">
                            {p.muridBasic === 0 ? 'PERCUMA' : `${formatRM(p.muridBasic)}`}
                          </p>
                        </div>
                        <div className="rounded-lg bg-white border border-black/10 px-3 py-2.5">
                          <p className="text-[10px] text-[#5c554d] mb-0.5">Basic · beli buku</p>
                          <p className="text-xl font-extrabold text-[#14110d]">
                            {p.bukuBasic === 0 ? 'PERCUMA' : `${formatRM(p.bukuBasic)}`}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => setShowBasic(false)}
                    className="mt-2 text-[11px] text-[#5c554d] hover:text-[#14110d] underline underline-offset-2"
                  >
                    simpan
                  </button>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </SubScene>
  );
};

// Servis Zabra — benefit cards + on-field photo gallery with lightbox.
const ServisScene = () => {
  const [lightbox, setLightbox] = useState(null); // index or null

  return (
    <SubScene title="Servis Zabra">
      <p className="text-center text-base text-gray-300 mb-6 max-w-2xl mx-auto">
        Kami turun padang — pemasangan, latihan &amp; sokongan dibuat terus di sekolah. Berikut
        antara sesi servis &amp; latihan kami bersama sekolah.
      </p>

      {/* benefit cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
        {SERVIS.map((b, i) => {
          const Icon = b.icon;
          return (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              className="bg-white border border-black/10 rounded-xl p-4 text-left"
            >
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                style={{ background: `${ACCENT}33` }}
              >
                <Icon size={20} style={{ color: ACCENT_LIGHT }} />
              </div>
              <h4 className="text-[#14110d] font-bold text-base mb-1">{b.title}</h4>
              <p className="text-sm text-[#5c554d]">{b.desc}</p>
            </motion.div>
          );
        })}
      </div>

      {/* on-field gallery */}
      <p className="text-sm uppercase tracking-wider text-[#6d28d9] font-bold mb-3 text-center">
        Sesi di sekolah
      </p>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
        {SERVIS_PHOTOS.map((src, i) => (
          <motion.button
            key={src}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: i * 0.03 }}
            onClick={() => setLightbox(i)}
            className="group relative aspect-square overflow-hidden rounded-xl border border-black/10 bg-white"
          >
            <img
              src={src}
              alt={`Servis Zabra ${i + 1}`}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
            />
          </motion.button>
        ))}
      </div>

      {/* lightbox */}
      {lightbox !== null &&
        createPortal(
          <div
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[120] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <button
              onClick={() => setLightbox(null)}
              className="absolute top-4 right-4 text-[#14110d]/80 hover:text-[#14110d] bg-black/[0.05] rounded-full p-2"
              aria-label="Tutup"
            >
              <X size={22} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightbox((v) => (v - 1 + SERVIS_PHOTOS.length) % SERVIS_PHOTOS.length);
              }}
              className="absolute left-3 md:left-8 text-[#14110d]/80 hover:text-[#14110d] bg-black/[0.05] rounded-full p-2"
              aria-label="Sebelum"
            >
              <ArrowLeft size={22} />
            </button>
            <img
              key={lightbox}
              src={SERVIS_PHOTOS[lightbox]}
              alt={`Servis Zabra ${lightbox + 1}`}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-[90vw] rounded-xl shadow-2xl object-contain"
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightbox((v) => (v + 1) % SERVIS_PHOTOS.length);
              }}
              className="absolute right-3 md:right-8 text-[#14110d]/80 hover:text-[#14110d] bg-black/[0.05] rounded-full p-2 rotate-180"
              aria-label="Seterusnya"
            >
              <ArrowLeft size={22} />
            </button>
          </div>,
          document.body
        )}
    </SubScene>
  );
};

/* ---- Device mockups for the Kelebihan showcase ------------------------- */
// iPhone SE frame — classic home-button phone (top/bottom bezels, ~9:16 screen).
const PhoneFrameSE = ({ src, alt }) => (
  <div className="relative mx-auto w-full max-w-[164px]">
    <div className="relative rounded-[1.6rem] bg-[#111] px-[6px] pt-6 pb-9 shadow-[0_18px_40px_-14px_rgba(0,0,0,0.6)] ring-1 ring-black/40">
      {/* side buttons */}
      <span className="absolute -left-[2px] top-[64px] h-10 w-[3px] rounded-l bg-[#2b2b2b]" />
      <span className="absolute -right-[2px] top-[54px] h-8 w-[3px] rounded-r bg-[#2b2b2b]" />
      {/* earpiece speaker (top bezel) */}
      <div className="absolute left-1/2 top-2.5 h-1 w-9 -translate-x-1/2 rounded-full bg-[#2b2b2b]" />
      {/* screen */}
      <div className="relative overflow-hidden rounded-[3px] bg-white aspect-[9/16]">
        <img src={src} alt={alt} loading="lazy" className="absolute inset-0 h-full w-full object-contain object-top" />
        <div className="pointer-events-none absolute -left-1/3 top-0 z-10 h-full w-1/3 -skew-x-12 bg-white/10" />
      </div>
      {/* home button (bottom bezel) */}
      <div className="absolute left-1/2 bottom-2 h-6 w-6 -translate-x-1/2 rounded-full border-2 border-[#2b2b2b]" />
    </div>
  </div>
);

// Modern iPhone frame (notch, tall ~9:19.5 screen).
const PhoneFrameModern = ({ src, alt }) => (
  <div className="relative mx-auto w-full max-w-[172px]">
    {/* body */}
    <div className="relative rounded-[2rem] bg-[#111] p-[6px] shadow-[0_18px_40px_-14px_rgba(0,0,0,0.6)] ring-1 ring-black/40">
      {/* side buttons */}
      <span className="absolute -left-[2px] top-[86px] h-9 w-[3px] rounded-l bg-[#2b2b2b]" />
      <span className="absolute -right-[2px] top-[70px] h-12 w-[3px] rounded-r bg-[#2b2b2b]" />
      {/* screen */}
      <div className="relative overflow-hidden rounded-[1.6rem] bg-black aspect-[9/19.5]">
        {/* notch */}
        <div className="absolute left-1/2 top-1.5 z-20 h-4 w-16 -translate-x-1/2 rounded-full bg-black" />
        <img src={src} alt={alt} loading="lazy" className="absolute inset-0 h-full w-full object-cover object-top" />
        {/* subtle glare */}
        <div className="pointer-events-none absolute -left-1/3 top-0 z-10 h-full w-1/3 -skew-x-12 bg-white/10" />
      </div>
    </div>
  </div>
);

// Pick the phone frame by variant ('se' | default modern).
const PhoneFrame = ({ src, alt, variant }) =>
  variant === 'se' ? <PhoneFrameSE src={src} alt={alt} /> : <PhoneFrameModern src={src} alt={alt} />;

// PC monitor frame for landscape (admin / kiosk) screenshots.
const MonitorFrame = ({ src, alt }) => (
  <div className="w-full select-none">
    <div className="rounded-xl border-[3px] border-[#111] bg-[#111] p-1.5 shadow-[0_16px_36px_-16px_rgba(0,0,0,0.6)]">
      <div className="relative overflow-hidden rounded-md bg-white aspect-[16/10]">
        {/* full screenshot, top-anchored; white fills any short bottom gap */}
        <img src={src} alt={alt} loading="lazy" className="absolute inset-0 h-full w-full object-contain object-top" />
        <div className="pointer-events-none absolute -left-1/3 top-0 z-10 h-full w-1/3 -skew-x-12 bg-white/10" />
      </div>
    </div>
    {/* stand */}
    <div className="mx-auto h-3.5 w-12 rounded-b-md bg-[#111]" />
    <div className="mx-auto h-2 w-28 rounded-full bg-[#111]" />
  </div>
);

// Kelebihan Sistem Zabra — categorized product screenshots with captions + lightbox.
const KelebihanScene = ({
  showcase = KELEBIHAN_SHOWCASE,
  title = 'Kelebihan Litera PSS',
  intro = 'Pantas, moden & mesra pengguna — direka mengikut rubrik KPM. Terokai keupayaan sebenar sistem melalui paparan sebenar di bawah.',
}) => {
  // flat list — each shot carries its group meta (for slide labels / device type)
  const flat = showcase.flatMap((g) =>
    g.shots.map((s) => ({ ...s, grpTitle: g.title, portrait: !!g.portrait, phone: g.phone }))
  );
  const [lightbox, setLightbox] = useState(null);
  const openShot = (src) => setLightbox(flat.findIndex((f) => f.src === src));

  // View mode: 'slides' (big, tabs + next/next) is the default; 'grid' shows all.
  const [view, setView] = useState('slides');
  // Slide state: which group tab + which shot within that group.
  const [gi, setGi] = useState(0);
  const [si, setSi] = useState(0);
  const grp = showcase[gi] || showcase[0];
  const shots = grp.shots;
  const cur = shots[si];
  const goShot = (d) => setSi((v) => (v + d + shots.length) % shots.length);
  const pickGroup = (n) => {
    setGi(n);
    setSi(0);
  };

  const ViewToggle = () => (
    <div className="flex justify-center mb-6">
      <div className="inline-flex rounded-full border border-white/20 bg-white/10 p-1">
        {[
          { k: 'slides', label: 'Paparan Slaid' },
          { k: 'grid', label: 'Lihat Semua' },
        ].map((o) => (
          <button
            key={o.k}
            onClick={() => setView(o.k)}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${
              view === o.k ? 'bg-white text-[#14110d]' : 'text-white/80 hover:text-white'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );

  if (view === 'slides') {
    return (
      <SubScene title={title}>
        <p className="text-center text-base text-gray-300 mb-5 max-w-2xl mx-auto">{intro}</p>
        <ViewToggle />

        {/* GROUP TABS */}
        <div className="flex flex-wrap justify-center gap-2 mb-5">
          {showcase.map((g, n) => {
            const on = n === gi;
            return (
              <button
                key={g.key}
                onClick={() => pickGroup(n)}
                className="px-4 py-2 rounded-xl text-sm font-semibold border transition"
                style={
                  on
                    ? { background: ACCENT, color: '#fff', borderColor: ACCENT }
                    : { background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.85)', borderColor: 'rgba(255,255,255,0.18)' }
                }
              >
                {g.title}
                <span className={`ml-2 text-[11px] ${on ? 'text-white/80' : 'text-white/50'}`}>
                  {g.shots.length}
                </span>
              </button>
            );
          })}
        </div>

        <div className="relative max-w-5xl mx-auto">
          {/* group blurb + per-group counter */}
          <div className="flex items-center justify-between gap-3 mb-3 px-1">
            <p className="text-sm text-white/70 max-w-2xl">{grp.blurb}</p>
            <span className="shrink-0 text-sm font-semibold text-white/85">
              {si + 1} / {shots.length}
            </span>
          </div>

          {/* stage: BIG device + caption, side arrows */}
          <div className="relative rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-8 sm:px-14 sm:py-10">
            <AnimatePresence mode="wait">
              <motion.div
                key={cur.src}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.25 }}
                className="flex flex-col items-center"
              >
                <button onClick={() => openShot(cur.src)} className="w-full flex justify-center" title="Klik untuk besarkan penuh skrin">
                  {grp.portrait ? (
                    <div className="w-[280px] sm:w-[340px]">
                      <PhoneFrame src={cur.src} alt={cur.cap} variant={grp.phone} />
                    </div>
                  ) : (
                    <div className="w-full max-w-[860px]">
                      <MonitorFrame src={cur.src} alt={cur.cap} />
                    </div>
                  )}
                </button>
                <p className="text-center text-white text-base sm:text-lg mt-6 max-w-2xl leading-snug">{cur.cap}</p>
                <p className="text-center text-white/50 text-xs mt-1">Ketik gambar untuk paparan penuh</p>
              </motion.div>
            </AnimatePresence>

            {shots.length > 1 && (
              <>
                <button
                  onClick={() => goShot(-1)}
                  aria-label="Sebelum"
                  className="absolute left-1 sm:-left-5 top-1/2 -translate-y-1/2 bg-white text-[#14110d] rounded-full p-3 shadow-lg hover:scale-105 transition"
                >
                  <ArrowLeft size={22} />
                </button>
                <button
                  onClick={() => goShot(1)}
                  aria-label="Seterusnya"
                  className="absolute right-1 sm:-right-5 top-1/2 -translate-y-1/2 bg-white text-[#14110d] rounded-full p-3 shadow-lg hover:scale-105 transition rotate-180"
                >
                  <ArrowLeft size={22} />
                </button>
              </>
            )}
          </div>

          {/* dots — within this group only */}
          {shots.length > 1 && (
            <div className="flex flex-wrap items-center justify-center gap-1.5 mt-5">
              {shots.map((_, d) => (
                <button
                  key={d}
                  onClick={() => setSi(d)}
                  aria-label={`Slaid ${d + 1}`}
                  className="h-2.5 rounded-full transition-all"
                  style={{
                    width: d === si ? 24 : 9,
                    background: d === si ? ACCENT_LIGHT : 'rgba(255,255,255,0.3)',
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* lightbox */}
        {lightbox !== null &&
          createPortal(
            <div
              onClick={() => setLightbox(null)}
              className="fixed inset-0 z-[120] bg-black/90 backdrop-blur-sm flex flex-col items-center justify-center p-4"
            >
              <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 text-white/80 hover:text-white bg-white/10 rounded-full p-2" aria-label="Tutup">
                <X size={22} />
              </button>
              <img src={flat[lightbox].src} alt={flat[lightbox].cap} onClick={(e) => e.stopPropagation()} className="max-h-[86vh] max-w-[96vw] rounded-xl shadow-2xl object-contain" />
              <p className="text-base text-white mt-3 text-center max-w-xl">{flat[lightbox].cap}</p>
            </div>,
            document.body
          )}
      </SubScene>
    );
  }

  return (
    <SubScene title={title}>
      <p className="text-center text-base text-gray-300 mb-6 max-w-2xl mx-auto">
        {intro}
      </p>
      <ViewToggle />

      <div className="space-y-8">
        {showcase.map((grp, gi) => {
          const Icon = grp.icon;
          return (
            <motion.section
              key={grp.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: gi * 0.08 }}
            >
              <div className="flex items-start gap-3 mb-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: `${ACCENT}33` }}
                >
                  <Icon size={20} style={{ color: ACCENT_LIGHT }} />
                </div>
                <div>
                  <h4 className="text-white font-bold text-xl leading-tight">{grp.title}</h4>
                  <p className="text-sm text-white/75 max-w-2xl">{grp.blurb}</p>
                </div>
              </div>

              <div
                className={`grid ${
                  grp.portrait
                    ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-6'
                    : 'grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-8'
                }`}
              >
                {grp.shots.map((shot) => (
                  <button
                    key={shot.src}
                    onClick={() => openShot(shot.src)}
                    className="group text-center transition hover:-translate-y-1"
                  >
                    {grp.portrait ? (
                      <PhoneFrame src={shot.src} alt={shot.cap} variant={grp.phone} />
                    ) : (
                      <MonitorFrame src={shot.src} alt={shot.cap} />
                    )}
                    <p className="text-[11px] text-white/80 px-2 pt-3 leading-snug">{shot.cap}</p>
                  </button>
                ))}
              </div>
            </motion.section>
          );
        })}
      </div>

      {/* lightbox — portal to body so it centers over the viewport */}
      {lightbox !== null &&
        createPortal(
          <div
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[120] bg-black/90 backdrop-blur-sm flex flex-col items-center justify-center p-4"
          >
            <button
              onClick={() => setLightbox(null)}
              className="absolute top-4 right-4 text-[#14110d]/80 hover:text-[#14110d] bg-black/[0.05] rounded-full p-2"
              aria-label="Tutup"
            >
              <X size={22} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightbox((v) => (v - 1 + flat.length) % flat.length);
              }}
              className="absolute left-3 md:left-8 text-[#14110d]/80 hover:text-[#14110d] bg-black/[0.05] rounded-full p-2"
              aria-label="Sebelum"
            >
              <ArrowLeft size={22} />
            </button>
            <img
              key={lightbox}
              src={flat[lightbox].src}
              alt={flat[lightbox].cap}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[80vh] max-w-[92vw] rounded-xl shadow-2xl object-contain"
            />
            <p className="text-base text-[#3a352e] mt-3 text-center max-w-xl">{flat[lightbox].cap}</p>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightbox((v) => (v + 1) % flat.length);
              }}
              className="absolute right-3 md:right-8 text-[#14110d]/80 hover:text-[#14110d] bg-black/[0.05] rounded-full p-2 rotate-180"
              aria-label="Seterusnya"
            >
              <ArrowLeft size={22} />
            </button>
          </div>,
          document.body
        )}
    </SubScene>
  );
};

// Interactive 3D matrik card — flip on click, tilt on hover.
const FlipCard3D = () => {
  const [flipped, setFlipped] = useState(false);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [qrOpen, setQrOpen] = useState(false); // QR child (apps) toggle
  const [openBenefits, setOpenBenefits] = useState({}); // per-benefit child toggles
  const [variantKey, setVariantKey] = useState('berlogo'); // 'berlogo' | 'bergambar'
  const ref = useRef(null);
  const toggleBenefit = (i) => setOpenBenefits((o) => ({ ...o, [i]: !o[i] }));

  const variant = CARD_VARIANTS.find((v) => v.key === variantKey) || CARD_VARIANTS[0];
  const switchVariant = (k) => {
    setVariantKey(k);
    setFlipped(false); // reset to front on switch
    setQrOpen(false);
  };

  const handleMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5; // -0.5..0.5
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ x: -py * 16, y: px * 16 }); // rotateX, rotateY (deg)
  };
  const reset = () => setTilt({ x: 0, y: 0 });

  const cardFace =
    'absolute inset-0 rounded-2xl overflow-hidden';
  const faceImg = 'w-full h-full object-cover';
  // keep the card artwork bright & clean on the light background
  const brightFilter = { filter: 'brightness(1.04) saturate(1.03)' };

  return (
    <div className="relative flex flex-col items-center py-4">
      {/* variant tabs (above the glow, clean & readable on the dark page) */}
      <div className="relative z-20 mb-4 inline-flex rounded-full bg-white/10 border border-white/20 backdrop-blur-md p-1">
        {CARD_VARIANTS.map((v) => {
          const on = v.key === variantKey;
          return (
            <button
              key={v.key}
              onClick={() => switchVariant(v.key)}
              className="text-sm font-semibold px-4 py-1.5 rounded-full transition"
              style={on ? { background: ACCENT, color: '#fff' } : { color: '#ddd6fe' }}
            >
              {v.label}
            </button>
          );
        })}
      </div>

      {/* soft top highlight — sits behind the CARD (below the tabs) */}
      <div
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-16 w-[420px] max-w-[95vw] h-[300px]"
        style={{
          background:
            'radial-gradient(55% 50% at 50% 0%, rgba(255,255,255,0.5), transparent 70%)',
          filter: 'blur(6px)',
        }}
      />
      {/* soft warm halo behind the card (subtle on light bg) */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[360px] h-[360px] rounded-full"
        style={{ background: `radial-gradient(circle, ${ACCENT}22, transparent 68%)` }}
        animate={{ opacity: [0.3, 0.5, 0.3], scale: [0.96, 1.06, 0.96] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      />

      <div style={{ perspective: 1400 }} className="relative">
        {/* gentle levitation + glow shadow (kept off the 3D layer) */}
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
          style={{ filter: `drop-shadow(0 16px 30px rgba(20,17,13,0.18)) drop-shadow(0 0 18px ${ACCENT}33)` }}
        >
          <motion.div
            ref={ref}
            onMouseMove={handleMove}
            onMouseLeave={reset}
            onClick={() => setFlipped((f) => !f)}
            animate={{ rotateY: (flipped ? 180 : 0) + tilt.y, rotateX: tilt.x }}
            transition={{ type: 'spring', stiffness: 120, damping: 14 }}
            whileHover={{ scale: 1.04 }}
            className="relative cursor-pointer"
            style={{
              width: 300,
              maxWidth: '82vw',
              aspectRatio: '0.63',
              transformStyle: 'preserve-3d',
            }}
          >
            {/* FRONT */}
            <div className={cardFace} style={{ backfaceVisibility: 'hidden' }}>
              <img src={variant.front} alt={`${variant.label} — depan`} className={faceImg} style={brightFilter} />
              <span className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-black/10" />
            </div>
            {/* BACK */}
            <div
              className={cardFace}
              style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
            >
              <img src={variant.back} alt={`${variant.label} — belakang`} className={faceImg} style={brightFilter} />
              <span className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-black/10" />
            </div>
          </motion.div>
        </motion.div>

        {/* QR annotation callout — only for variants with a QR (berlogo) */}
        <AnimatePresence>
          {!flipped && variant.hasQr && (
            <motion.div
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -8 }}
              transition={{ duration: 0.35 }}
              className="hidden md:flex items-start absolute left-full"
              style={{ pointerEvents: 'none', top: '2%' }}
            >
              {/* connector starts at the card's right edge (no overlap on artwork) */}
              <span
                className="block rounded-full"
                style={{ width: 9, height: 9, background: ACCENT_LIGHT, boxShadow: `0 0 10px ${ACCENT_LIGHT}` }}
              />
              <span
                className="block h-px"
                style={{ width: 30, background: `linear-gradient(90deg, ${ACCENT_LIGHT}, ${ACCENT})` }}
              />
              {/* branch card */}
              <div
                className="relative w-64 rounded-xl border p-4 backdrop-blur-md"
                style={{ background: `linear-gradient(160deg, #ffffff, #f5f3ff)`, borderColor: `${ACCENT}66`, pointerEvents: 'auto' }}
              >
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[14px] font-bold uppercase tracking-wide text-[#14110d]">
                    1 QR — 3 Kegunaan
                  </p>
                  <button
                    onClick={() => setQrOpen((o) => !o)}
                    className="text-[#14110d]/70 hover:text-[#14110d] bg-black/[0.05] rounded-full p-1 transition shrink-0"
                    title={qrOpen ? 'Sembunyi' : 'Lihat aplikasi'}
                  >
                    {qrOpen ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                <ul className="space-y-1.5">
                  {[
                    ['DELIMa', 'Log masuk pantas'],
                    ['AINS', 'Terus tanpa taip'],
                    ['2 Sistem Litera', 'Pinjam-Pulang & Kehadiran'],
                  ].map(([t2, s]) => (
                    <li key={t2} className="flex items-start gap-2">
                      <Check size={16} className="mt-0.5 shrink-0" style={{ color: ACCENT_LIGHT }} />
                      <span className="text-[16px] text-[#3a352e]">
                        <span className="font-semibold text-[#14110d]">{t2}</span>
                        <span className="block text-[13px] text-[#5c554d]">{s}</span>
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-[13px] font-semibold text-[#6d28d9]">Imbas, selesai. ⚡</p>

                {/* CHILD — absolute overlay to the right; does NOT move the parent */}
                <AnimatePresence>
                  {qrOpen && (
                    <motion.div
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -8 }}
                      transition={{ duration: 0.3 }}
                      className="absolute left-full top-0 flex items-start"
                      style={{ pointerEvents: 'auto' }}
                    >
                      <span className="block h-px shrink-0 mt-6" style={{ width: 20, background: ACCENT_LIGHT }} />
                      <div
                        className="w-72 rounded-2xl border p-3 backdrop-blur-xl space-y-3"
                        style={{ background: `linear-gradient(160deg, #ffffff, #f5f3ff)`, borderColor: `${ACCENT}88`, boxShadow: `0 20px 45px -18px ${ACCENT}aa` }}
                      >
                        {QR_APPS.map((app) => (
                          <div
                            key={app.name}
                            className="rounded-xl overflow-hidden border border-black/10 bg-white"
                          >
                            {/* big logo banner */}
                            <div className="w-full bg-white flex items-center justify-center p-2" style={{ height: 72 }}>
                              <img
                                src={app.logo}
                                alt={app.name}
                                className="max-w-full max-h-full object-contain"
                              />
                            </div>
                            <div className="p-2.5">
                              <p className="text-[15px] font-extrabold text-[#14110d] leading-tight">{app.name}</p>
                              <p className="text-[12px] text-[#5c554d] leading-snug mt-0.5">{app.desc}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Barcode annotation callout — points from barcode (front) to a branch on the right */}
        <AnimatePresence>
          {!flipped && (
            <motion.div
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -8 }}
              transition={{ duration: 0.35, delay: 0.05 }}
              className="hidden md:flex items-start absolute left-full"
              style={{ pointerEvents: 'none', bottom: '2%' }}
            >
              <span
                className="block rounded-full"
                style={{ width: 9, height: 9, background: TIER_COLORS[2], boxShadow: `0 0 10px ${TIER_COLORS[2]}` }}
              />
              <span
                className="block h-px"
                style={{ width: 30, background: `linear-gradient(90deg, ${TIER_COLORS[2]}, ${TIER_COLORS[3]})` }}
              />
              <div
                className="w-64 rounded-xl border-2 p-4 bg-white shadow-lg"
                style={{ borderColor: `${TIER_COLORS[2]}` }}
              >
                <p className="text-[14px] font-bold uppercase tracking-wide text-[#14110d] mb-1.5">
                  Barkod ID @ slogan murid/guru
                </p>
                <p className="text-[16px] text-[#3a352e] leading-snug">
                  tambahan info yang juga boleh di scan dalam sistem zabra —
                </p>
                <p className="mt-2 text-[13px] font-semibold text-[#15803d]">
                  tiada caj tambahan. 💚
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* LEFT — bukan sekadar kad sistem: kegunaan menyeluruh (dari PDF kelebihan kad) */}
        <AnimatePresence>
          {!flipped && (
            <motion.div
              initial={{ opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 8 }}
              transition={{ duration: 0.35 }}
              className="hidden md:flex items-center absolute right-full top-1/2 -translate-y-1/2"
              style={{ pointerEvents: 'none', top: '10%', transform: 'translateY(-50%)' }}
            >
              {/* panel: title on top + chips (whole block vertically centered) */}
              <div>
                <p className="text-right text-[14px] font-bold uppercase tracking-wide text-[#6d28d9] mb-2">
                  Kelebihan Kad
                </p>
                <div className="space-y-2">
                {KAD_BENEFITS.map((b, i) => {
                  const Icon = b.icon;
                  const on = !!openBenefits[i];
                  return (
                    <div
                      key={b.t}
                      className="rounded-xl border p-3 backdrop-blur-md text-left w-72"
                      style={{
                        background: `linear-gradient(160deg, #ffffff, #f5f3ff)`,
                        borderColor: `${ACCENT}55`,
                        pointerEvents: 'auto',
                      }}
                    >
                      <div className="flex items-start gap-2.5">
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                          style={{ background: `${ACCENT}44` }}
                        >
                          <Icon size={18} style={{ color: ACCENT_LIGHT }} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[15px] font-semibold text-[#14110d] leading-tight">{b.t}</p>
                          <p className="text-[13px] text-[#5c554d] leading-snug">{b.s}</p>
                        </div>
                        <button
                          onClick={() => toggleBenefit(i)}
                          className="text-[#14110d]/70 hover:text-[#14110d] bg-black/[0.05] rounded-full p-1 transition shrink-0"
                          title={on ? 'Sembunyi' : 'Lihat lagi'}
                        >
                          {on ? <EyeOff size={15} /> : <Eye size={15} />}
                        </button>
                      </div>
                      {/* details expand inline below (no overlap; panel re-centres) */}
                      <AnimatePresence>
                        {on && (
                          <motion.ul
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="overflow-hidden mt-2 pt-2 border-t border-black/10 space-y-1"
                          >
                            {b.details.map((d, di) => (
                              <li key={di} className="flex items-start gap-1.5">
                                <Check size={14} className="mt-0.5 shrink-0" style={{ color: ACCENT_LIGHT }} />
                                <span className="text-[13px] text-[#3a352e] leading-snug">{d}</span>
                              </li>
                            ))}
                          </motion.ul>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
                </div>
              </div>

              {/* white connector — vertically centred on the whole panel.
                  line + dot, dot sits at the card's left edge (matching the right side gap) */}
              <span
                className="block h-px shrink-0"
                style={{ width: 30, background: 'rgba(255,255,255,0.85)', marginLeft: 8 }}
              />
              <span
                className="block rounded-full shrink-0"
                style={{ width: 9, height: 9, background: '#ffffff', boxShadow: '0 0 10px rgba(255,255,255,0.9)' }}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* ===== BACK-SIDE callouts (shown when flipped) — top / middle / bottom breakdown ===== */}
        <AnimatePresence>
          {flipped && (
            <>
              {[
                {
                  top: '14%',
                  icon: ShieldCheck,
                  title: 'Bahagian Atas · Keselamatan',
                  body: 'Notis "kad tidak boleh dipindah milik" & arahan pulangkan ke pejabat sekolah jika ditemui.',
                },
                {
                  top: '46%',
                  icon: Home,
                  title: 'Bahagian Tengah · Alamat Sekolah',
                  body: 'Nama penuh, alamat & nombor telefon rasmi sekolah — mudah dihubungi & disahkan.',
                },
                {
                  top: '78%',
                  icon: Sparkles,
                  title: 'Bahagian Bawah · Slogan & Media Sosial',
                  body: 'Sekolah pilih slogan sendiri + QR ke platform pilihan — TikTok, Facebook atau YouTube untuk promosi PSS.',
                },
              ].map((z) => {
                const Icon = z.icon;
                return (
                  <motion.div
                    key={z.title}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -8 }}
                    transition={{ duration: 0.35 }}
                    className="hidden md:flex items-center absolute left-full"
                    style={{ pointerEvents: 'none', top: z.top, transform: 'translateY(-50%)' }}
                  >
                    <span
                      className="block rounded-full"
                      style={{ width: 9, height: 9, background: ACCENT_LIGHT, boxShadow: `0 0 10px ${ACCENT_LIGHT}` }}
                    />
                    <span
                      className="block h-px"
                      style={{ width: 30, background: `linear-gradient(90deg, ${ACCENT_LIGHT}, ${ACCENT})` }}
                    />
                    <div
                      className="w-64 rounded-xl border p-3.5 backdrop-blur-md"
                      style={{ background: `linear-gradient(160deg, #ffffff, #f5f3ff)`, borderColor: `${ACCENT}66` }}
                    >
                      <p className="flex items-center gap-1.5 text-[14px] font-bold uppercase tracking-wide text-[#14110d] mb-1">
                        <Icon size={16} style={{ color: ACCENT_LIGHT }} /> {z.title}
                      </p>
                      <p className="text-[14px] text-[#3a352e] leading-snug">{z.body}</p>
                    </div>
                  </motion.div>
                );
              })}
            </>
          )}
        </AnimatePresence>

        {/* reflective floor */}
        <div
          className="pointer-events-none absolute left-1/2 -translate-x-1/2 -bottom-6 w-56 h-8 rounded-[50%]"
          style={{ background: 'radial-gradient(ellipse, rgba(20,17,13,0.18), transparent 70%)', filter: 'blur(6px)' }}
        />
      </div>

      <button
        onClick={() => setFlipped((f) => !f)}
        className="relative mt-8 inline-flex items-center gap-2 text-[#ddd6fe] hover:text-white text-sm bg-white/10 border border-white/20 rounded-full px-4 py-1.5 transition"
      >
        <CreditCard size={14} /> {flipped ? 'Lihat Depan' : 'Lihat Belakang'} · tekan kad untuk balik
      </button>
    </div>
  );
};

// Exclusive vertical price card: icon, name, crossed original + special price.
const PriceCard = ({ name, sub, ori, now, featured, tag, badge, index = 0 }) => {
  const showOri = ori && ori > now;
  const pct = showOri ? Math.round(((ori - now) / ori) * 100) : 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08, type: 'spring', stiffness: 130, damping: 16 }}
      whileHover={{ y: -6 }}
      className="relative flex flex-col items-center text-center rounded-2xl bg-white pt-7 pb-6 px-5 overflow-hidden"
      style={{
        border: featured ? `2px solid ${ACCENT}` : '1px solid rgba(0,0,0,0.10)',
        boxShadow: featured
          ? `0 22px 45px -20px ${ACCENT}cc`
          : '0 14px 30px -22px rgba(20,17,13,0.4)',
      }}
    >
      {/* top ribbon */}
      <span
        className="absolute top-0 inset-x-0 h-1.5"
        style={{ background: featured ? `linear-gradient(90deg, ${ACCENT}, #a78bfa)` : '#e5dcc9' }}
      />
      {tag && (
        <span
          className="absolute top-3 right-3 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide text-white px-2 py-0.5 rounded-full"
          style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT}aa)` }}
        >
          <Star size={9} className="fill-white" /> {tag}
        </span>
      )}

      {/* icon */}
      <span
        className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3"
        style={{ background: `${ACCENT}1a` }}
      >
        <CreditCard size={24} style={{ color: ACCENT }} />
      </span>

      <p className="text-lg font-extrabold text-[#14110d] leading-tight">{name}</p>
      {sub && <p className="text-[11px] text-[#5c554d] mb-2">{sub}</p>}

      {/* price */}
      <div className="mt-2 flex items-baseline gap-2">
        {showOri && (
          <span className="text-base font-semibold text-[#9a938a] line-through decoration-red-400 decoration-2">
            {formatRM(ori)}
          </span>
        )}
        <span className="text-3xl font-extrabold text-[#14110d]">{formatRM(now)}</span>
      </div>
      {showOri && (
        <span className="mt-2 text-[10px] font-bold text-white bg-red-500 rounded-full px-2.5 py-0.5">
          JIMAT {pct}%
        </span>
      )}
      {badge && !showOri && (
        <span className="mt-2 text-[10px] font-semibold text-[#5c554d]">{badge}</span>
      )}
    </motion.div>
  );
};

const KadScene = ({ config }) => {
  const [showPrice, setShowPrice] = useState(false);
  const p = config.kadPrices;

  return (
    <SubScene title="Kad Zabra (Kad Matrik)">
      <p className="text-center text-base text-gray-300 mb-3 max-w-xl mx-auto">
        Optimumkan penggunaan sistem untuk aktiviti yang lebih digital dan lancar.
      </p>

      <AnimatePresence mode="wait">
        {!showPrice ? (
          <motion.div
            key="card"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <FlipCard3D />
            {/* down-arrow → price view */}
            <div className="flex flex-col items-center ">
              <button
                onClick={() => setShowPrice(true)}
                className="flex flex-col items-center gap-1 text-[#ddd6fe] hover:text-white transition"
              >
                <span className="text-sm font-semibold">Lihat Harga</span>
                <span className="w-9 h-9 rounded-full bg-white/10 border border-white/20 flex items-center justify-center ">
                  <ChevronDown size={18} />
                </span>
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="price"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.25 }}
            className="max-w-4xl mx-auto"
          >
            {/* vertical price cards — recommended pair emphasised */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 items-stretch">
              <PriceCard
                index={0}
                name="Kad Berlogo"
                sub="Kad Matrik"
                tag="Disyorkan"
                featured
                ori={p.berlogo.ori}
                now={p.berlogo.now}
              />
              <PriceCard
                index={1}
                name="Kad Bergambar"
                sub="Kad Matrik"
                tag="Disyorkan"
                featured
                ori={p.bergambar.ori}
                now={p.bergambar.now}
              />
              <PriceCard
                index={2}
                name="Kad Laminate"
                sub="Pilihan bajet"
                badge="Jimat & ringkas"
                ori={p.laminate.ori}
                now={p.laminate.now}
              />
              <PriceCard
                index={3}
                name="Poket & Tali"
                sub="Add-on pilihan"
                badge="Tambahan"
                ori={p.pokettali.ori}
                now={p.pokettali.now}
              />
            </div>

            {/* up-arrow → back to card */}
            <div className="flex flex-col items-center mt-8">
              <button
                onClick={() => setShowPrice(false)}
                className="flex flex-col items-center gap-1 text-[#ddd6fe] hover:text-white transition"
              >
                <span className="w-9 h-9 rounded-full bg-white/10 border border-white/20 flex items-center justify-center">
                  <ChevronDown size={18} className="rotate-180" />
                </span>
                <span className="text-sm font-semibold">Lihat Kad</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </SubScene>
  );
};

// Full-screen image lightbox (portal) — reused by galleries.
const PhotoLightbox = ({ photos, index, setIndex, alt = 'Gambar' }) => {
  if (index === null) return null;
  return createPortal(
    <div
      onClick={() => setIndex(null)}
      className="fixed inset-0 z-[120] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <button
        onClick={() => setIndex(null)}
        className="absolute top-4 right-4 text-[#14110d]/80 hover:text-[#14110d] bg-black/[0.05] rounded-full p-2"
        aria-label="Tutup"
      >
        <X size={22} />
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIndex((v) => (v - 1 + photos.length) % photos.length);
        }}
        className="absolute left-3 md:left-8 text-[#14110d]/80 hover:text-[#14110d] bg-black/[0.05] rounded-full p-2"
        aria-label="Sebelum"
      >
        <ArrowLeft size={22} />
      </button>
      <img
        key={index}
        src={photos[index]}
        alt={`${alt} ${index + 1}`}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[88vh] max-w-[92vw] rounded-xl shadow-2xl object-contain"
      />
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIndex((v) => (v + 1) % photos.length);
        }}
        className="absolute right-3 md:right-8 text-[#14110d]/80 hover:text-[#14110d] bg-black/[0.05] rounded-full p-2 rotate-180"
        aria-label="Seterusnya"
      >
        <ArrowLeft size={22} />
      </button>
    </div>,
    document.body
  );
};

const TestimoniScene = () => {
  const [lightbox, setLightbox] = useState(null);
  return (
    <SubScene title="Kata Mereka">
      <p className="text-center text-base text-gray-300 mb-6 max-w-2xl mx-auto">
        Bukan sekadar kata-kata — ini luahan sebenar guru &amp; pegawai PPD. Selama 13 tahun,
        reputasi kami dibina sepenuhnya melalui cadangan dari satu sekolah ke sekolah lain.
      </p>

      {/* masonry-style wall of WhatsApp praise screenshots */}
      <div className="columns-2 md:columns-3 lg:columns-4 gap-3 [column-fill:_balance]">
        {TESTIMONI_PHOTOS.map((src, i) => (
          <motion.button
            key={src}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.03 }}
            onClick={() => setLightbox(i)}
            className="group mb-3 block w-full overflow-hidden rounded-xl border border-black/10 bg-white hover:border-[#10b981]/60 transition"
          >
            <img
              src={src}
              alt={`Testimoni ${i + 1}`}
              loading="lazy"
              className="w-full transition-transform duration-300 group-hover:scale-[1.03]"
            />
          </motion.button>
        ))}
      </div>

      <PhotoLightbox
        photos={TESTIMONI_PHOTOS}
        index={lightbox}
        setIndex={setLightbox}
        alt="Testimoni"
      />
    </SubScene>
  );
};

/* =========================================================================
   ADMIN PANEL — global pricing config (session only)
   ========================================================================= */
const NumberField = ({ label, value, onChange, prefix = 'RM', step = 50 }) => (
  <label className="flex items-center justify-between gap-3 py-1.5">
    <span className="text-sm text-[#5c554d]">{label}</span>
    <span className="flex items-center gap-1 bg-black/[0.05] border border-black/10 rounded-lg px-2 focus-within:border-[#10b981]">
      {prefix && <span className="text-[11px] text-[#5c554d]">{prefix}</span>}
      <input
        type="number"
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-20 bg-transparent text-[#14110d] text-right text-base py-1.5 outline-none"
      />
    </span>
  </label>
);

const AdminPanel = ({ open, onClose, config, setConfig, resetConfig }) => {
  const setTier = (key, i, val) => {
    const next = [...config[key]];
    next[i] = val;
    setConfig({ ...config, [key]: next });
  };
  const setKad = (item, field, val) =>
    setConfig({
      ...config,
      kadPrices: { ...config.kadPrices, [item]: { ...config.kadPrices[item], [field]: val } },
    });

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm"
          />
          <motion.div
            initial={{ x: -360, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -360, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 30 }}
            className="fixed top-0 left-0 bottom-0 z-[70] w-[340px] max-w-[88vw] bg-white border-r border-black/10 shadow-2xl overflow-y-auto"
          >
            <div className="sticky top-0 bg-white/95 backdrop-blur-md px-5 py-4 flex items-center justify-between border-b border-black/10">
              <h3 className="text-[#14110d] font-bold flex items-center gap-2">
                <Settings size={18} className="text-[#6d28d9]" /> Tetapan Harga
              </h3>
              <button onClick={onClose} className="text-[#5c554d] hover:text-[#14110d]">
                <X size={20} />
              </button>
            </div>

            <div className="px-5 py-4 space-y-6">
              <p className="text-[11px] text-[#5c554d]">
                Tukar nilai di sini untuk kemas kini semua harga serta-merta. Tetapan ini sementara
                — kembali ke asal apabila halaman dimuat semula.
              </p>

              {/* Sistem initial tiers */}
              <section>
                <p className="text-sm font-bold uppercase tracking-wide text-[#6d28d9] mb-1">
                  Pakej Sistem Pusat Sumber Digital — Tahun Pertama
                </p>
                {TIERS.map((tr, i) => (
                  <NumberField
                    key={tr.key}
                    label={tr.label}
                    value={config.sistemInitialTiers[i]}
                    onChange={(v) => setTier('sistemInitialTiers', i, v)}
                  />
                ))}
                <NumberField
                  label="Harga asal (potong) · 0 = tiada"
                  value={config.sistemInitialOriginal}
                  onChange={(v) => setConfig({ ...config, sistemInitialOriginal: v })}
                />
              </section>

              {/* Buku initial tiers */}
              <section>
                <p className="text-sm font-bold uppercase tracking-wide text-[#6d28d9] mb-1">
                  Pakej Buku — Tahun Pertama
                </p>
                {TIERS.map((tr, i) => (
                  <NumberField
                    key={tr.key}
                    label={tr.label}
                    value={config.bukuInitialTiers[i]}
                    onChange={(v) => setTier('bukuInitialTiers', i, v)}
                  />
                ))}
              </section>

              {/* Mod langganan sistem — kadar tetap vs per murid */}
              <section>
                <p className="text-sm font-bold uppercase tracking-wide text-[#6d28d9] mb-2">
                  Langganan Sistem — Mod
                </p>
                <div className="grid grid-cols-2 gap-2 mb-1">
                  {[
                    { key: 'fixed', label: 'Kadar Tetap' },
                    { key: 'murid', label: 'Bayar Per Murid' },
                  ].map((m) => {
                    const on = (config.renewalMode || 'fixed') === m.key;
                    return (
                      <button
                        key={m.key}
                        type="button"
                        onClick={() => setConfig({ ...config, renewalMode: m.key })}
                        className="text-sm font-semibold py-2 rounded-lg border transition"
                        style={
                          on
                            ? { background: '#7c3aed', color: '#fff', borderColor: '#7c3aed' }
                            : { background: '#fff', color: '#5c554d', borderColor: 'rgba(0,0,0,0.12)' }
                        }
                      >
                        {m.label}
                      </button>
                    );
                  })}
                </div>

                {(config.renewalMode || 'fixed') === 'fixed' ? (
                  <>
                    <p className="text-[11px] font-semibold text-[#5c554d] mt-3 mb-1">
                      Kadar tetap tahunan (ikut tier)
                    </p>
                    {TIERS.map((tr, i) => (
                      <NumberField
                        key={tr.key}
                        label={tr.label}
                        value={(config.sistemRenewalFixedTiers || [300, 500, 900, 1100])[i]}
                        onChange={(v) => setTier('sistemRenewalFixedTiers', i, v)}
                      />
                    ))}
                  </>
                ) : (
                  <>
                    <p className="text-[11px] font-semibold text-[#5c554d] mt-3 mb-1">
                      Kadar per murid
                    </p>
                    <NumberField
                      label="Kadar / murid"
                      prefix="RM"
                      step={0.1}
                      value={config.sistemRenewalPerStudent}
                      onChange={(v) => setConfig({ ...config, sistemRenewalPerStudent: v })}
                    />
                    <NumberField
                      label="Kadar asal / murid (potong)"
                      prefix="RM"
                      step={0.1}
                      value={config.renewalPerStudentOriginal}
                      onChange={(v) => setConfig({ ...config, renewalPerStudentOriginal: v })}
                    />
                    <NumberField
                      label="Bayaran minimum"
                      step={50}
                      value={config.sistemRenewalMin}
                      onChange={(v) => setConfig({ ...config, sistemRenewalMin: v })}
                    />
                    <NumberField
                      label="Had maksimum"
                      value={config.sistemRenewalMax}
                      onChange={(v) => setConfig({ ...config, sistemRenewalMax: v })}
                    />
                  </>
                )}
              </section>

              {/* Renewal buku tiers (Premium base) */}
              <section>
                <p className="text-sm font-bold uppercase tracking-wide text-[#6d28d9] mb-1">
                  Langganan Premium — Beli Buku
                </p>
                {TIERS.map((tr, i) => (
                  <NumberField
                    key={tr.key}
                    label={tr.label}
                    value={config.bukuRenewalTiers[i]}
                    onChange={(v) => setTier('bukuRenewalTiers', i, v)}
                  />
                ))}
                <p className="text-[10px] text-[#5c554d] mt-2">
                  Basic = separuh harga Premium (Rahmah Eksklusif Basic = percuma). Dikira automatik.
                </p>
              </section>

              {/* Digitera — tahun pertama */}
              <section>
                <p className="text-sm font-bold uppercase tracking-wide text-[#0d9488] mb-1">
                  Digitera — Tahun Pertama
                </p>
                {TIERS.map((tr, i) => (
                  <NumberField
                    key={tr.key}
                    label={tr.label}
                    value={(config.digiteraInitialTiers || [2500, 3200, 3900, 4500])[i]}
                    onChange={(v) => setTier('digiteraInitialTiers', i, v)}
                  />
                ))}
                <NumberField
                  label="Harga asal (potong)"
                  value={config.digiteraInitialOriginal}
                  onChange={(v) => setConfig({ ...config, digiteraInitialOriginal: v })}
                />
              </section>

              {/* Digitera — langganan tahunan */}
              <section>
                <p className="text-sm font-bold uppercase tracking-wide text-[#0d9488] mb-1">
                  Digitera — Langganan Tahunan
                </p>
                {TIERS.map((tr, i) => (
                  <NumberField
                    key={tr.key}
                    label={tr.label}
                    value={(config.digiteraRenewalTiers || [400, 600, 1000, 1200])[i]}
                    onChange={(v) => setTier('digiteraRenewalTiers', i, v)}
                  />
                ))}
                <p className="text-[10px] text-[#5c554d] mt-2">
                  Digitera = sistem kawalan sekolah (produk berasingan). Basic = separuh harga.
                </p>
              </section>

              {/* EzBook — tahun pertama */}
              <section>
                <p className="text-sm font-bold uppercase tracking-wide text-[#059669] mb-1">
                  EzBook — Tahun Pertama
                </p>
                {TIERS.map((tr, i) => (
                  <NumberField
                    key={tr.key}
                    label={tr.label}
                    value={(config.ezbookInitialTiers || [1200, 1500, 1900, 2300])[i]}
                    onChange={(v) => setTier('ezbookInitialTiers', i, v)}
                  />
                ))}
                <NumberField
                  label="Harga asal (potong)"
                  value={config.ezbookInitialOriginal}
                  onChange={(v) => setConfig({ ...config, ezbookInitialOriginal: v })}
                />
              </section>

              {/* EzBook — langganan tahunan */}
              <section>
                <p className="text-sm font-bold uppercase tracking-wide text-[#059669] mb-1">
                  EzBook — Langganan Tahunan
                </p>
                {TIERS.map((tr, i) => (
                  <NumberField
                    key={tr.key}
                    label={tr.label}
                    value={(config.ezbookRenewalTiers || [300, 400, 600, 800])[i]}
                    onChange={(v) => setTier('ezbookRenewalTiers', i, v)}
                  />
                ))}
                <p className="text-[10px] text-[#5c554d] mt-2">
                  EzBook = sistem tempahan bilik (produk berasingan). Basic = separuh harga.
                </p>
              </section>

              {/* Item disertakan — kuantiti */}
              <section>
                <p className="text-sm font-bold uppercase tracking-wide text-[#6d28d9] mb-1">
                  Item Disertakan
                </p>
                <NumberField
                  label="Scanner 2D"
                  prefix=""
                  step={1}
                  value={config.scannerQty}
                  onChange={(v) => setConfig({ ...config, scannerQty: Math.max(1, Math.round(v)) })}
                />
                <p className="text-[10px] text-[#5c554d] mt-2">
                  Bilangan Scanner 2D yang sekolah dapat (cth: “2× Scanner 2D”). Terpakai pada halaman pakej &amp; sebut harga.
                </p>
              </section>

              {/* Promosi Pakej Buku — potong harga tahun pertama */}
              <section>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-bold uppercase tracking-wide text-[#6d28d9]">
                    Promosi Pakej Buku
                  </p>
                  <button
                    type="button"
                    onClick={() => setConfig({ ...config, promoEnabled: !config.promoEnabled })}
                    aria-pressed={!!config.promoEnabled}
                    className="relative inline-flex h-6 w-11 items-center rounded-full transition"
                    style={{ background: config.promoEnabled ? '#7c3aed' : 'rgba(0,0,0,0.18)' }}
                  >
                    <span
                      className="inline-block h-5 w-5 transform rounded-full bg-white transition"
                      style={{ transform: config.promoEnabled ? 'translateX(22px)' : 'translateX(2px)' }}
                    />
                  </button>
                </div>
                <label className="flex items-center justify-between gap-3 py-1.5">
                  <span className="text-sm text-[#5c554d] shrink-0">Nama promosi</span>
                  <input
                    type="text"
                    value={config.promoName}
                    onChange={(e) => setConfig({ ...config, promoName: e.target.value })}
                    className="flex-1 min-w-0 bg-black/[0.05] border border-black/10 rounded-lg px-2 py-1.5 text-[#14110d] text-sm text-right outline-none focus-within:border-[#10b981]"
                  />
                </label>
                <NumberField
                  label="Potongan harga (RM)"
                  prefix="− RM"
                  step={50}
                  value={config.promoCut}
                  onChange={(v) => setConfig({ ...config, promoCut: Math.max(0, v) })}
                />
                <p className="text-[10px] text-[#5c554d] mt-2">
                  Bila AKTIF &amp; potongan &gt; 0, setiap tier Pakej Buku (tahun pertama) ditolak jumlah ini. Terpakai pada halaman pakej &amp; sebut harga tahun pertama sahaja. Pakej Sistem tidak terjejas.
                </p>
              </section>

              {/* Kad Matrik prices — original (potong) + istimewa */}
              <section>
                <p className="text-sm font-bold uppercase tracking-wide text-[#6d28d9] mb-1">
                  Harga Kad Matrik
                </p>
                {[
                  ['berlogo', 'Kad Berlogo'],
                  ['bergambar', 'Kad Bergambar'],
                  ['laminate', 'Kad Laminate'],
                  ['pokettali', 'Poket & Tali'],
                ].map(([key, label]) => (
                  <div key={key} className="mb-1.5">
                    <p className="text-[11px] text-[#5c554d] mb-0.5">{label}</p>
                    <div className="grid grid-cols-2 gap-2">
                      <NumberField
                        label="Asal"
                        step={0.5}
                        value={config.kadPrices[key].ori}
                        onChange={(v) => setKad(key, 'ori', v)}
                      />
                      <NumberField
                        label="Istimewa"
                        step={0.5}
                        value={config.kadPrices[key].now}
                        onChange={(v) => setKad(key, 'now', v)}
                      />
                    </div>
                  </div>
                ))}
                <p className="text-[10px] text-[#5c554d] mt-1">
                  Harga potong hanya dipapar jika “Asal” &gt; “Istimewa”.
                </p>
              </section>

              <button
                onClick={resetConfig}
                className="w-full text-base font-semibold text-[#14110d] bg-black/[0.05] hover:bg-black/10 border border-black/15 rounded-lg py-2.5 transition"
              >
                Set Semula ke Asal
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

/* =========================================================================
   SEBUT HARGA — export (print to PDF) with letterhead
   ========================================================================= */
const COMPANY = {
  name: 'ZABRA SOLUTIONS',
  reg: 'IP0402065-T',
  address: '2-09, UTC SELANGOR @ ANGGERIK MALL, JALAN 14/8, SEKSYEN 14, 40000 SHAH ALAM SELANGOR',
  hp: '+6017-363 4766',
  tel: '+03-5880 8363',
  email: 'zabrasolutions@gmail.com',
  logo: LOGO_URL, // official logo (Supabase)
  // company stamp/cop — resolved to an absolute URL at build time so it loads
  // in the print pop-up and the downloaded Word file.
  stamp: '/apps/cop%20zabra%20jpeg.jpg',
};

// Absolute URL for assets referenced from the generated quote (print window /
// Word download have no page origin of their own).
const absUrl = (path) => {
  try {
    return new URL(path, window.location.origin).href;
  } catch {
    return path;
  }
};

const MONTHS_MS = [
  'Januari', 'Februari', 'Mac', 'April', 'Mei', 'Jun',
  'Julai', 'Ogos', 'September', 'Oktober', 'November', 'Disember',
];

const rm = (n) => Math.round(Number(n) || 0).toLocaleString('en-MY');

// Build the quote HTML. mode: 'first' | 'renewal'. forDownload omits the auto-print script.
const buildQuoteHTML = ({ schoolName, schoolAddress, pkg, prices, students, config, dateStr, quoteNo, isoDate = '', mode = 'first', includeSistem = true, includeMurid = true, forDownload = false }) => {
  const isRenewal = mode === 'renewal';
  // Solo products (Digitera, EzBook) get a single-product quote layout.
  const isSoloQuote = isSoloProduct(pkg);
  const soloInit = packageInitial(pkg, prices, config);
  const soloPromo = soloInit.price;
  const soloAsal = soloInit.original;
  const soloRenew = soloRenewalValue(pkg, prices);

  // === Bayaran permulaan (tahun pertama) — both packages, Harga Asal → Promosi ===
  // Numbers are live from the app: promo = tier price now; asal = the anchor.
  const sistemPromo = prices.sistemInitial;
  const sistemAsal = config.sistemInitialOriginal;          // e.g. RM6,500
  const bukuPromo = prices.bukuInitial;                     // already post-promo
  // Buku anchor: pre-promo tier price when a promo is active, else Standard tier.
  const bukuPromoActive = Boolean(prices.promoActive);
  const bukuAsal = bukuPromoActive ? prices.bukuInitialBase : config.bukuInitialTiers[2];

  // Bayaran servis tahunan (subscription) — value framing
  const muridRenew = prices.muridPremium;
  const bukuRenew = prices.bukuPremium;
  const renewPerStudent = config.sistemRenewalPerStudent; // e.g. 1.2
  const renewPerStudentOld = config.renewalPerStudentOriginal; // e.g. 3 (harga asal)
  // crossed-out original per-murid rate, matching the app card (~~RM3~~ RM1.20)
  const perMuridRateHtml =
    renewPerStudentOld > renewPerStudent
      ? `<span class="strike">RM${renewPerStudentOld}</span> <b>RM${renewPerStudent}</b>`
      : `<b>RM${renewPerStudent}</b>`;

  // Subscription mode: fixed annual rate (by tier) vs per-murid.
  const renewalIsFixed = prices.renewalMode === 'fixed';
  // Label + description for the "sistem" subscription option, mode-aware.
  const sistemRenewLabel = renewalIsFixed ? 'Kadar tetap tahunan' : 'Bayar per murid';
  // shared description line used in both the renewal quote and the lampiran.
  const perMuridDesc = renewalIsFixed
    ? `Kadar tetap tahunan · ${prices.tier.label} — RM ${rm(muridRenew)}/tahun`
    : prices.sistemRenewalFloored
    ? `Kadar minimum servis RM${rm(config.sistemRenewalMin)}/tahun bagi sekolah kecil`
    : `${perMuridRateHtml}/murid AKTIF setahun (≈ RM ${rm(muridRenew)}/tahun) — hanya murid aktif dikira`;

  // what's included when renewing (elaboration of "servis diteruskan").
  const renewMuridScope = 'Naik taraf system + servis/konsultan diteruskan + servis data diteruskan';
  const renewBukuScope = 'Naik taraf koleksi + naik taraf sistem + servis/konsultan diteruskan + servis data diteruskan';

  const docTitle = isRenewal ? 'SEBUT HARGA — TAHUN BERIKUTNYA (SUBSCRIPTIONS)' : 'SEBUT HARGA — TAHUN PERTAMA';

  const esc = (x) =>
    String(x).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // strike-through "asal" cell — only shown when it's genuinely higher than promo
  const asalCell = (asal, promo) =>
    asal > promo ? `<span class="strike">RM ${rm(asal)}.00</span>` : '—';

  // === Lampiran (pg 2 & 3): package detail sheet — mirrors the app card ===
  // For each package: tahun-pertama price band, feature groups, and the
  // "tahun seterusnya" (langganan) options. All numbers are live from `prices`.
  const buildLampiran = (p, promo, asal, note, lampNo) => {
    const pct =
      asal > promo ? Math.round(((asal - promo) / asal) * 100) : 0;
    const feats = p.groups
      .map((g) => {
        // "Buku Bernilai RMxxxx" tracks the live tahun-pertama promo price,
        // not the hardcoded RM4,700.
        const title = /^Buku Bernilai/i.test(g.title)
          ? `Buku Bernilai RM${rm(promo)}`
          : g.title;
        return `<div class="col"><h4>${esc(title)}</h4><ul>${g.items
          .map((it) => `<li>${esc(applyItemConfig(it, config))}</li>`)
          .join('')}</ul></div>`;
      })
      .join('');

    // tahun seterusnya (langganan premium) — per murid vs beli buku.
    // Uses the shared floor-aware, crossed-anchor per-murid description.
    const perMuridLine = perMuridDesc;

    return `
  <div class="pagebreak"></div>
  <div class="lamp-h">
    <div class="n">LAMPIRAN ${lampNo}</div>
    <div class="t">· ${esc(p.name)} ·</div>
    <div class="s">${esc(p.subname || '')}</div>
  </div>

  <div class="price-band">
    <div class="lbl">Tahun pertama · Akaun Premium disertakan</div>
    <div>${asal > promo ? `<span class="old">RM ${rm(asal)}.00</span><span class="save">JIMAT ${pct}%</span>` : ''}</div>
    <div class="now">RM ${rm(promo)}.00</div>
    <div class="lbl">${esc(note)}</div>
  </div>

  <div class="sub-title">Apa yang sekolah dapat:</div>
  <div class="feat">${feats}</div>

  <div class="sub-title">Langganan tahun seterusnya (tanpa kontrak)</div>
  <table>
    <thead>
      <tr><th style="width:70%">Pilihan pembaharuan</th><th class="r">Setahun (RM)</th></tr>
    </thead>
    <tbody>
      ${
        isSoloProduct(p)
          ? `<tr>
        <td>Langganan tahunan<br/><small>Kadar tetap tahunan · ${esc(prices.tier.label)}</small><br/><small>${renewMuridScope}</small></td>
        <td class="r promo">RM ${rm(soloRenewalValue(p, prices))}.00</td>
      </tr>`
          : `<tr>
        <td>${esc(sistemRenewLabel)}<br/><small>${perMuridLine}</small><br/><small>${renewMuridScope}</small></td>
        <td class="r promo">RM ${rm(muridRenew)}.00</td>
      </tr>
      <tr>
        <td>Beli buku PSS<br/><small>${renewBukuScope}</small></td>
        <td class="r">RM ${rm(bukuRenew)}.00</td>
      </tr>`
      }
    </tbody>
  </table>
  <div class="note">
    * Bayar untuk tahun seterusnya hanya jika sekolah berpuas hati — tiada ikatan, tiada paksaan.<br/>
    ${
      isSoloProduct(p)
        ? `* Kadar tetap tahunan mengikut kategori sekolah.<br/>`
        : `* Kadar per murid dikira berdasarkan <b>bilangan MURID AKTIF sebenar</b> pada masa pembaharuan. GURU &amp; ahli tidak aktif TIDAK dikira.<br/>`
    }
    * Boleh dibiayai melalui peruntukan PCG / PIBG atau kedua-duanya.
  </div>`;
  };

  // Lampiran 2 for the renewal quote — elaborates "servis diteruskan".
  const RENEWAL_SERVICE_GROUPS = [
    {
      title: 'Servis / Konsultan',
      items: [
        'Servis Cloud (pengkomputeran awan)',
        'Servis Check Komputer',
        'Sesi Latihan Kegunaan Sistem',
        'Pemasangan & Setup',
        'Backup & Pemindahan Data',
      ],
    },
    {
      title: 'Data Servis',
      items: [
        'Convert & Clean Data dari sistem lama',
        'Masukkan Data Murid terkini (siap diguna)',
        'System Upgrade tahunan (mengikut feedback Guru Media & Jabatan)',
      ],
    },
  ];

  const buildRenewalLampiran = () => {
    const feats = RENEWAL_SERVICE_GROUPS.map(
      (g) => `<div class="col"><h4>${esc(g.title)}</h4><ul>${g.items
        .map((it) => `<li>${esc(it)}</li>`)
        .join('')}</ul></div>`
    ).join('');
    return `
  <div class="pagebreak"></div>
  <div class="lamp-h">
    <div class="n">LAMPIRAN 2</div>
    <div class="t">· Servis Yang Diteruskan ·</div>
    <div class="s">Termasuk dalam bayaran servis tahunan (subscription)</div>
  </div>
  <div class="sub-title">Setiap pembaharuan tahunan merangkumi servis berikut:</div>
  <div class="feat">${feats}</div>
  <div class="note" style="margin-top:18px;">
    * Semua servis di atas diteruskan sepanjang tempoh langganan aktif.<br/>
    * Tanpa kontrak — pembaharuan adalah pilihan sekolah. Sambung hanya jika berpuas hati.
  </div>`;
  };

  // Lampiran 4 — Kelebihan Sistem Zabra (feature showcase with screenshots).
  // Screenshots need absolute URLs to load in the print window / Word doc.
  const buildKelebihanLampiran = (lampNo, showcase = KELEBIHAN_SHOWCASE, heading = 'Kelebihan Litera PSS', sub = 'Paparan sebenar sistem Pusat Sumber Digital') => {
    const shotHtml = (g, s) => {
      const img = `<img src="${absUrl(s.src)}" alt="${esc(s.cap)}"/>`;
      const device = g.portrait
        ? (g.phone === 'se'
            ? `<div class="phone phone-se"><span class="ear"></span><div class="pscreen">${img}</div><span class="home"></span></div>`
            : `<div class="phone"><span class="notch"></span>${img}</div>`)
        : `<div class="monitor"><div class="mscreen">${img}</div><div class="mneck"></div><div class="mbase"></div></div>`;
      const shotCls = g.portrait ? 'keleb-shot keleb-shot--portrait' : 'keleb-shot';
      return `<div class="${shotCls}">${device}<div class="cap">${esc(s.cap)}</div></div>`;
    };

    // Break each topic into page-sized chunks so every page starts with the
    // heading (never a bare image) — guarantees the top gap always shows, and
    // landscape groups with >6 images spill onto extra pages cleanly.
    let firstBlock = true;
    const blocksHtml = [];
    showcase.forEach((g) => {
      const perPage = g.portrait ? 8 : 6; // 4×2 phones, or 2×3 monitors
      const gridCls = g.portrait ? 'keleb-shots keleb-shots--portrait' : 'keleb-shots';
      const total = g.shots.length;
      for (let start = 0; start < total; start += perPage) {
        const chunk = g.shots.slice(start, start + perPage);
        const isCont = start > 0;
        const shots = chunk.map((s) => shotHtml(g, s)).join('');
        // first block of the whole lampiran shares the title page; all others
        // start a fresh page (so they always lead with text → top gap holds).
        const grpCls = firstBlock ? 'keleb-grp' : 'keleb-grp keleb-grp--page';
        firstBlock = false;
        blocksHtml.push(
          `<div class="${grpCls}"><h3>${esc(g.title)}${isCont ? ' (sambungan)' : ''}</h3>${
            isCont ? '' : `<p class="bl">${esc(g.blurb)}</p>`
          }<div class="${gridCls}">${shots}</div></div>`
        );
      }
    });

    return `
  <div class="pagebreak"></div>
  <div class="lamp-h">
    <div class="n">LAMPIRAN ${lampNo}</div>
    <div class="t">· ${esc(heading)} ·</div>
    <div class="s">${esc(sub)}</div>
  </div>
  ${blocksHtml.join('')}`;
  };

  // First-year lampiran, renumbered so they stay sequential when Pakej Sistem
  // is excluded. Kelebihan Sistem always comes last.
  let firstLampiran = '';
  if (!isRenewal) {
    let n = 2;
    if (isSoloQuote) {
      const meta = SOLO_QUOTE_META[pkg.id];
      firstLampiran += buildLampiran(
        pkg, soloPromo, soloAsal, `harga ${prices.tier.label.toLowerCase()}`, n++
      );
      firstLampiran += buildKelebihanLampiran(n, meta.showcase, meta.kelebihanTitle, meta.kelebihanSub);
    } else {
      if (includeSistem) {
        firstLampiran += buildLampiran(
          PACKAGES.sistem, sistemPromo, sistemAsal, `harga ${prices.tier.label.toLowerCase()}`, n++
        );
      }
      firstLampiran += buildLampiran(
        PACKAGES.buku, bukuPromo, bukuAsal,
        bukuPromoActive
          ? `${prices.promoName} · sistem PERCUMA`
          : `harga ${prices.tier.label.toLowerCase()} · sistem PERCUMA`,
        n++
      );
      firstLampiran += buildKelebihanLampiran(n);
    }
  }

  const lampiran = isRenewal ? buildRenewalLampiran() : firstLampiran;

  // The document <title> becomes the browser's default "Save as PDF" filename —
  // mirror the .doc naming so PDFs are dated too.
  const safeTitle = (schoolName || 'sekolah').replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-');
  const docFileTitle = `${isoDate ? isoDate + ' ' : ''}SebutHarga-${
    mode === 'renewal' ? 'ServisTahunan' : 'TahunPertama'
  }-${safeTitle}`;

  // Accent colour follows the product theme (Litera purple / Digitera teal).
  const accent = (pkg.theme && pkg.theme.from) || '#7c3aed';

  return `<!doctype html><html><head><meta charset="utf-8"/>
<title>${esc(docFileTitle)}</title>
<style>
  :root { --accent: ${accent}; }
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color:#1a1a1a; margin:0; padding:0; }
  .sheet { max-width: 800px; margin: 0 auto; padding: 32px 40px; }
  .head { display:flex; align-items:flex-start; gap:18px; border-bottom:3px solid #F29200; padding-bottom:14px; }
  .head img { height:70px; width:auto; }
  .co h1 { margin:0; font-size:18px; letter-spacing:.5px; }
  .co .reg { font-size:11px; color:#555; margin:2px 0 6px; }
  .co p { margin:1px 0; font-size:10.5px; color:#333; }
  .title { text-align:center; font-size:20px; font-weight:bold; letter-spacing:3px; margin:22px 0 4px; }
  .meta { display:flex; justify-content:space-between; font-size:12px; margin:14px 0 18px; }
  .to { font-size:13px; margin-bottom:16px; }
  .to b { display:block; font-size:15px; margin-top:2px; }
  table { width:100%; border-collapse:collapse; font-size:12.5px; }
  th, td { border:1px solid #ccc; padding:9px 12px; text-align:left; }
  th { background:#F29200; color:#fff; }
  td.r, th.r { text-align:right; }
  .info td:first-child { width:38%; color:#444; background:#faf6ef; }
  .total td { font-weight:bold; font-size:14px; background:#fff7ea; }
  .note { font-size:10.5px; color:#666; margin-top:14px; line-height:1.5; }
  .sub-h { margin:22px 0 0; font-size:13px; font-weight:bold; color:#111; }
  small { color:#666; font-size:10px; }
  .value { margin-top:10px; background:#ecfdf5; border:1px solid #a7f3d0; border-radius:8px; padding:9px 12px; font-size:11.5px; color:#065f46; }
  .strike { text-decoration:line-through; color:#777; }
  .promo { font-weight:bold; }
  .promo-badge { display:inline-block; background:#ef4444; color:#fff; font-size:9.5px; font-weight:bold; padding:2px 8px; border-radius:9999px; margin-left:4px; white-space:nowrap; }
  .to .addr { display:block; font-size:12px; color:#444; font-weight:normal; margin-top:3px; line-height:1.4; }
  .pagebreak { page-break-before: always; padding-top:30px; }
  .lamp-h { text-align:center; margin:6px 0 2px; }
  .lamp-h .n { font-size:11px; letter-spacing:2px; color:var(--accent); font-weight:bold; }
  .lamp-h .t { font-size:20px; font-weight:bold; margin:2px 0 0; }
  .lamp-h .s { font-size:12px; color:#666; }
  .price-band { border:2px solid var(--accent); border-radius:10px; padding:12px 16px; margin:14px 0 18px; }
  .price-band .lbl { font-size:11px; color:#666; }
  .price-band .now { font-size:26px; font-weight:800; color:#111; }
  .price-band .old { font-size:13px; color:#999; text-decoration:line-through; margin-right:8px; }
  .price-band .save { display:inline-block; background:#ef4444; color:#fff; font-size:9px; font-weight:bold; padding:1px 7px; border-radius:9999px; vertical-align:middle; }
  .feat { display:flex; flex-wrap:wrap; gap:14px; }
  .feat .col { flex:1 1 45%; min-width:220px; }
  .feat h4 { font-size:10.5px; letter-spacing:.5px; color:var(--accent); font-weight:bold; margin:0 0 5px; text-transform:uppercase; }
  .feat ul { margin:0; padding:0; list-style:none; }
  .feat li { font-size:11.5px; color:#222; padding:2px 0 2px 15px; position:relative; }
  .feat li:before { content:"✓"; position:absolute; left:0; color:#16a34a; font-weight:bold; }
  .sub-title { font-size:12.5px; font-weight:bold; color:#111; margin:18px 0 8px; }
  /* Kelebihan Sistem showcase */
  .keleb-grp { margin:0 0 18px; padding-top:6px; }
  .keleb-grp--page { page-break-before: always; break-before: page; padding-top:8mm; }
  .keleb-grp h3 { font-size:14px; font-weight:bold; color:var(--accent); margin:0 0 2px; page-break-after:avoid; break-after:avoid; }
  .keleb-grp .bl { font-size:11px; color:#444; margin:0 0 8px; line-height:1.45; page-break-after:avoid; break-after:avoid; }
  .keleb-shots { display:flex; flex-wrap:wrap; gap:16px; align-items:flex-start; }
  .keleb-shot { flex:1 1 46%; min-width:230px; text-align:center; page-break-inside:avoid; break-inside:avoid; }
  .keleb-shot .cap { font-size:9.5px; color:#555; padding:5px 6px 0; line-height:1.35; }
  /* Monitor mockup (landscape — admin / kiosk).
     White screen bg so an image shorter than 16:10 letterboxes to white, not black. */
  .monitor { max-width:340px; margin:0 auto; }
  .monitor .mscreen { border:3px solid #111; border-radius:8px; background:#fff; overflow:hidden; }
  .monitor .mscreen img { width:100%; height:auto; display:block; }
  .monitor .mneck { width:34px; height:9px; background:#111; margin:0 auto; border-radius:0 0 4px 4px; }
  .monitor .mbase { width:96px; height:6px; background:#111; margin:0 auto; border-radius:9999px; }
  /* Phone mockup (portrait — mobile) */
  .keleb-shots--portrait { gap:18px; }
  .keleb-shot--portrait { flex:0 0 auto; width:140px; min-width:0; }
  .keleb-shot--portrait .cap { font-size:8.5px; }
  .phone { position:relative; width:126px; margin:0 auto; background:#111; border-radius:22px; padding:6px; }
  .phone img { width:100%; height:auto; display:block; border-radius:16px; }
  .phone .notch { position:absolute; top:9px; left:50%; transform:translateX(-50%); width:46px; height:11px; background:#111; border-radius:9999px; z-index:2; }
  /* iPhone SE (classic, home button, top/bottom bezels) */
  .phone-se { width:120px; border-radius:16px; padding:22px 6px 30px; }
  .phone-se .pscreen { background:#fff; border-radius:2px; overflow:hidden; }
  .phone-se .pscreen img { width:100%; height:auto; display:block; border-radius:0; }
  .phone-se .ear { position:absolute; top:9px; left:50%; transform:translateX(-50%); width:30px; height:4px; background:#2b2b2b; border-radius:9999px; }
  .phone-se .home { position:absolute; bottom:7px; left:50%; transform:translateX(-50%); width:18px; height:18px; border:2px solid #2b2b2b; border-radius:9999px; }
  .sign { margin-top:36px; font-size:12px; }
  .sign .cop { margin:6px 0 -6px; }
  .sign .cop img { width:90px; height:auto; }
  .sign .line { border-top:1px solid #333; width:220px; margin-top:4px; padding-top:4px; }
  /* Small page margin via @page. Some print dialogs override this to "None",
     so page-starting blocks ALSO carry their own top padding as a fallback. */
  @page { margin: 10mm 0; }
  @media print {
    .sheet { max-width:none; margin:0; padding:0 12mm; }
    /* every element that begins a fresh page keeps a guaranteed top gap even
       if the print dialog strips @page margins */
    .pagebreak { padding-top:8mm; }
    .keleb-grp--page { padding-top:8mm; }
    .noprint { display:none; }
  }
</style></head>
<body>
  <div class="sheet">
  <div class="head">
    <img src="${COMPANY.logo}" alt="logo" width="180" height="70" style="height:70px;width:auto;"/>
    <div class="co">
      <h1>${COMPANY.name}</h1>
      <div class="reg">(${COMPANY.reg})</div>
      <p>${esc(COMPANY.address)}</p>
      <p>H/P: ${COMPANY.hp} &nbsp;·&nbsp; TEL: ${COMPANY.tel} &nbsp;·&nbsp; Email: ${COMPANY.email}</p>
    </div>
  </div>

  <div class="title">${docTitle}</div>
  <div class="meta">
    <span><b>No. Rujukan:</b> ${esc(quoteNo)}</span>
    <span><b>Tarikh:</b> ${esc(dateStr)}</span>
  </div>

  <div class="to">Kepada:
    <b>${esc(schoolName || '________________________')}</b>
    ${schoolAddress ? `<span class="addr">${esc(schoolAddress).replace(/\n/g, '<br/>')}</span>` : ''}
  </div>

  <table class="info">
    <tbody>
      <tr><td>Kategori sekolah</td><td>${esc(prices.tier.label)}</td></tr>
      <tr><td>Bilangan murid</td><td>${Number(students).toLocaleString('en-MY')} murid yang tersedia</td></tr>
    </tbody>
  </table>

  ${
    isRenewal && isSoloQuote
      ? `
  <table style="margin-top:14px;">
    <thead>
      <tr><th style="width:62%">Langganan Tahunan ${esc(pkg.name)}</th><th class="r">Setahun (RM)</th></tr>
    </thead>
    <tbody>
      <tr>
        <td>Langganan tahunan<br/><small>Kadar tetap tahunan · ${esc(prices.tier.label)}</small><br/><small>${renewMuridScope}</small></td>
        <td class="r">RM ${rm(soloRenew)}.00</td>
      </tr>
    </tbody>
  </table>
  <div class="value">
    💡 Servis, sokongan &amp; naik taraf (UPGRADE) sistem sepanjang tahun. Kadar tetap tahunan mengikut kategori sekolah — mudah dianggarkan. Berbaloi &amp; tanpa kontrak.
  </div>
  <div class="note">
    * Ini adalah bayaran servis tahunan (subscription) untuk tahun berikutnya (${esc(pkg.name)}).<br/>
    * Kadar tetap tahunan mengikut kategori sekolah (${esc(prices.tier.label)}).<br/>
    * <b>Perincian servis yang diteruskan dinyatakan di Lampiran 2 (muka surat seterusnya).</b><br/>
    * Tanpa kontrak — pembaharuan adalah pilihan sekolah. Sambung hanya jika berpuas hati.<br/>
    * Sebut harga ini sah selama 30 hari dari tarikh dikeluarkan.
  </div>`
      : isRenewal
      ? `
  <table style="margin-top:14px;">
    <thead>
      <tr><th style="width:62%">Bayaran Servis Tahunan${includeMurid ? ' — pilih salah satu' : ''}</th><th class="r">Setahun (RM)</th></tr>
    </thead>
    <tbody>
      ${
        includeMurid
          ? `<tr>
        <td>${esc(sistemRenewLabel)}<br/><small>${perMuridDesc}</small><br/><small>${renewMuridScope}</small></td>
        <td class="r">RM ${rm(muridRenew)}.00</td>
      </tr>`
          : ''
      }
      <tr>
        <td>Beli buku PSS<br/><small>${renewBukuScope}</small></td>
        <td class="r">RM ${rm(bukuRenew)}.00</td>
      </tr>
    </tbody>
  </table>
  <div class="value">
    💡 Servis, sokongan &amp; naik taraf (UPGRADE) sistem sepanjang tahun.${
      renewalIsFixed
        ? ` Kadar tetap tahunan mengikut kategori sekolah — mudah dianggarkan.`
        : prices.sistemRenewalFloored
        ? ` Kadar minimum servis terpakai bagi sekolah kecil.`
        : ` Bayaran <b>hanya bagi MURID aktif sahaja</b> (Guru/staf percuma).`
    } Berbaloi &amp; tanpa kontrak.
  </div>
  <div class="note">
    * Ini adalah bayaran servis tahunan (subscription) untuk tahun berikutnya — pilih satu pilihan.<br/>
    ${
      renewalIsFixed
        ? `* Kadar tetap tahunan mengikut kategori sekolah (${esc(prices.tier.label)}).<br/>`
        : `* Kadar per murid dikira berdasarkan <b>bilangan ahli AKTIF sebenar dalam sistem</b> pada masa pembaharuan. Ahli yang telah tamat / tidak aktif TIDAK dikira.<br/>`
    }
    * <b>Perincian servis yang diteruskan dinyatakan di Lampiran 2 (muka surat seterusnya).</b><br/>
    * Tanpa kontrak — pembaharuan adalah pilihan sekolah. Sambung hanya jika berpuas hati.<br/>
    * Sebut harga ini sah selama 30 hari dari tarikh dikeluarkan.
  </div>`
      : isSoloQuote
      ? `
  <table style="margin-top:14px;">
    <thead>
      <tr>
        <th style="width:52%">Bayaran Permulaan</th>
        <th class="r" style="width:24%">Harga Asal</th>
        <th class="r" style="width:24%">Harga Promosi</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>${esc(pkg.name)}${pkg.subname ? ` — ${esc(pkg.subname)}` : ''}<br/><small>Akaun Premium penuh disertakan</small></td>
        <td class="r">${asalCell(soloAsal, soloPromo)}</td>
        <td class="r promo">RM ${rm(soloPromo)}.00</td>
      </tr>
    </tbody>
  </table>
  <div class="note">
    * Ini adalah bayaran permulaan (setup) &amp; langganan tahunan tahun pertama untuk ${esc(pkg.name)}.<br/>
    * Sila rujuk lampiran 2 (butiran produk) dan lampiran 3 (kelebihan sistem) yang disertakan.<br/>
    * Tanpa kontrak — pembaharuan tahun berikutnya adalah pilihan sekolah. Sambung hanya jika berpuas hati.<br/>
    * Sebut harga ini sah selama 30 hari dari tarikh dikeluarkan.
  </div>`
      : `
  <table style="margin-top:14px;">
    <thead>
      <tr>
        <th style="width:52%">Bayaran Permulaan${includeSistem ? ' — pilih salah satu' : ''}</th>
        <th class="r" style="width:24%">Harga Asal</th>
        <th class="r" style="width:24%">Harga Promosi</th>
      </tr>
    </thead>
    <tbody>
      ${
        includeSistem
          ? `<tr>
        <td>Pakej Beli Sistem<br/><small>Sistem Pusat Sumber Digital (Akaun Premium penuh)</small></td>
        <td class="r">${asalCell(sistemAsal, sistemPromo)}</td>
        <td class="r promo">RM ${rm(sistemPromo)}.00</td>
      </tr>`
          : ''
      }
      <tr${bukuPromoActive ? ' style="background:#fff7ed;"' : ''}>
        <td>Pakej Beli Buku${
          bukuPromoActive
            ? ` <span class="promo-badge">🎉 ${esc(prices.promoName)} · JIMAT RM${rm(prices.promoCut)}</span>`
            : ''
        }<br/><small>tambah koleksi bahan buku · sistem automasi PERCUMA<br/>(surat sumbangan akan diberikan)</small></td>
        <td class="r">${asalCell(bukuAsal, bukuPromo)}</td>
        <td class="r promo">RM ${rm(bukuPromo)}.00</td>
      </tr>
    </tbody>
  </table>
  <div class="note">
    * Ini adalah bayaran permulaan (setup) &amp; langganan tahunan tahun pertama${includeSistem ? ' — pilih salah satu pakej' : ''}.<br/>
    * ${
      includeSistem
        ? 'Sila rujuk lampiran 2 &amp; 3 (butiran pakej) dan lampiran 4 (kelebihan sistem) yang disertakan.'
        : 'Sila rujuk lampiran 2 (butiran pakej) dan lampiran 3 (kelebihan sistem) yang disertakan.'
    }<br/>
    * Tanpa kontrak — pembaharuan tahun berikutnya adalah pilihan sekolah. Sambung hanya jika berpuas hati.<br/>
    * Sebut harga ini sah selama 30 hari dari tarikh dikeluarkan.
  </div>`
  }

  <div class="sign">
    Disediakan oleh,
    <div class="cop"><img src="${absUrl(COMPANY.stamp)}" alt="cop" width="90" height="90" style="width:90px;height:auto;"/></div>
    <div class="line">${COMPANY.name}</div>
  </div>

  ${lampiran}
  </div>

  ${forDownload ? '' : '<script>window.onload = function(){ setTimeout(function(){ window.print(); }, 350); };</script>'}
</body></html>`;
};

const ExportModal = ({ open, onClose, pkg, students, prices, config, dateStr, quoteNo, isoDate }) => {
  const [schoolName, setSchoolName] = useState('');
  const [schoolAddress, setSchoolAddress] = useState('');
  const [mode, setMode] = useState('first'); // 'first' | 'renewal'
  const [includeSistem, setIncludeSistem] = useState(true); // Tahun Pertama: sertakan Pakej Sistem
  const [includeMurid, setIncludeMurid] = useState(true); // Servis Tahunan: sertakan Bayar per murid
  // Which PRODUCT this quote is for. Defaults from the opened package.
  // Solo products (digitera/ezbook) map to themselves; Litera packages → 'litera'.
  const initialProduct = SOLO_PRODUCTS.includes(pkg.id) ? pkg.id : 'litera';
  const [productId, setProductId] = useState(initialProduct);
  // reset the product choice each time the modal opens for a different package
  useEffect(() => {
    if (open) setProductId(SOLO_PRODUCTS.includes(pkg.id) ? pkg.id : 'litera');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pkg.id]);

  const isSoloQuoteModal = productId !== 'litera';
  // Resolve the package object used for the quote. Litera quotes are driven by
  // PACKAGES.sistem (the table renders both sistem + buku); solo by itself.
  const activePkg = isSoloQuoteModal ? PACKAGES[productId] : PACKAGES.sistem;

  const handlePrint = () => {
    const html = buildQuoteHTML({ schoolName, schoolAddress, pkg: activePkg, prices, students, config, dateStr, quoteNo, isoDate, mode, includeSistem, includeMurid });
    const w = window.open('', '_blank');
    if (!w) {
      alert('Sila benarkan pop-up untuk menjana sebut harga.');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    onClose();
  };

  // Editable Word (.doc) download — HTML-based .doc opens & edits in Word.
  const handleWord = () => {
    const html = buildQuoteHTML({
      schoolName, schoolAddress, pkg: activePkg, prices, students, config, dateStr, quoteNo, isoDate, mode, includeSistem, includeMurid, forDownload: true,
    });
    const blob = new Blob(['﻿', html], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const safe = (schoolName || 'sekolah').replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-');
    a.href = url;
    a.download = `${isoDate} SebutHarga-${mode === 'renewal' ? 'ServisTahunan' : 'TahunPertama'}-${safe}.doc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    onClose();
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm"
          />
          <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-md max-h-[90vh] overflow-y-auto bg-white border border-black/10 rounded-2xl p-6 shadow-2xl"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[#14110d] font-bold flex items-center gap-2">
                <Sparkles size={18} className="text-[#6d28d9]" /> Jana Sebut Harga
              </h3>
              <button onClick={onClose} className="text-[#5c554d] hover:text-[#14110d]">
                <X size={20} />
              </button>
            </div>

            <label className="block text-sm text-[#5c554d] mb-1.5">Nama sekolah</label>
            <input
              autoFocus
              value={schoolName}
              onChange={(e) => setSchoolName(e.target.value)}
              placeholder="cth: SK Bandar Tun Razak (2)"
              className="w-full px-4 py-2.5 rounded-lg bg-black/[0.05] text-[#14110d] placeholder-gray-500 outline-none border border-black/10 focus:border-[#10b981] mb-4"
            />

            <label className="block text-sm text-[#5c554d] mb-1.5">Alamat sekolah <span className="text-[#9c958c]">(pilihan)</span></label>
            <textarea
              value={schoolAddress}
              onChange={(e) => setSchoolAddress(e.target.value)}
              rows={2}
              placeholder="cth: Jalan Midah Tengah 1, Taman Midah, 56000 Cheras, WP Kuala Lumpur"
              className="w-full px-4 py-2.5 rounded-lg bg-black/[0.05] text-[#14110d] placeholder-gray-500 outline-none border border-black/10 focus:border-[#10b981] mb-4 resize-none"
            />

            {/* produk — Litera PSS / Digitera / EzBook */}
            <label className="block text-sm text-[#5c554d] mb-1.5">Produk</label>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {[
                { key: 'litera', label: 'Litera PSS', c: '#7c3aed' },
                { key: 'digitera', label: 'Digitera', c: '#0d9488' },
                { key: 'ezbook', label: 'EzBook', c: '#059669' },
              ].map((pr) => {
                const on = productId === pr.key;
                return (
                  <button
                    key={pr.key}
                    onClick={() => setProductId(pr.key)}
                    className="text-sm font-semibold py-2 rounded-lg border transition"
                    style={
                      on
                        ? { background: pr.c, color: '#fff', borderColor: pr.c }
                        : { background: '#fff', color: '#5c554d', borderColor: 'rgba(0,0,0,0.12)' }
                    }
                  >
                    {pr.label}
                  </button>
                );
              })}
            </div>

            {/* jenis sebut harga */}
            <label className="block text-sm text-[#5c554d] mb-1.5">Jenis sebut harga</label>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {[
                { key: 'first', label: 'Tahun Pertama' },
                { key: 'renewal', label: 'Subscriptions (Servis Tahunan)' },
              ].map((m) => {
                const on = mode === m.key;
                return (
                  <button
                    key={m.key}
                    onClick={() => setMode(m.key)}
                    className="text-sm font-semibold py-2 rounded-lg border transition"
                    style={
                      on
                        ? { background: '#7c3aed', color: '#fff', borderColor: '#7c3aed' }
                        : { background: '#fff', color: '#5c554d', borderColor: 'rgba(0,0,0,0.12)' }
                    }
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>

            {/* Pilihan sertakan — bergantung jenis sebut harga.
                Solo products (Digitera/EzBook) ialah produk tunggal — tiada pilihan sertakan. */}
            {isSoloQuoteModal ? null : mode === 'first' ? (
              <div className="flex items-center justify-between gap-3 mb-4 bg-black/[0.03] border border-black/10 rounded-lg px-3 py-2.5">
                <div>
                  <p className="text-sm text-[#14110d] font-semibold">Sertakan Pakej Sistem</p>
                  <p className="text-[11px] text-[#5c554d]">Pakej Buku sentiasa disertakan.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setIncludeSistem((v) => !v)}
                  aria-pressed={includeSistem}
                  className="relative inline-flex h-6 w-11 items-center rounded-full transition shrink-0"
                  style={{ background: includeSistem ? '#7c3aed' : 'rgba(0,0,0,0.18)' }}
                >
                  <span
                    className="inline-block h-5 w-5 rounded-full bg-white transition"
                    style={{ transform: includeSistem ? 'translateX(22px)' : 'translateX(2px)' }}
                  />
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3 mb-4 bg-black/[0.03] border border-black/10 rounded-lg px-3 py-2.5">
                <div>
                  <p className="text-sm text-[#14110d] font-semibold">Sertakan “Bayar per murid”</p>
                  <p className="text-[11px] text-[#5c554d]">Pilihan Beli buku sentiasa disertakan.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setIncludeMurid((v) => !v)}
                  aria-pressed={includeMurid}
                  className="relative inline-flex h-6 w-11 items-center rounded-full transition shrink-0"
                  style={{ background: includeMurid ? '#7c3aed' : 'rgba(0,0,0,0.18)' }}
                >
                  <span
                    className="inline-block h-5 w-5 rounded-full bg-white transition"
                    style={{ transform: includeMurid ? 'translateX(22px)' : 'translateX(2px)' }}
                  />
                </button>
              </div>
            )}

            <div className="text-[12px] text-[#5c554d] bg-white border border-black/10 rounded-lg p-3 space-y-1 mb-5">
              <div className="flex justify-between"><span>Produk</span><span className="text-[#14110d] font-semibold">{isSoloQuoteModal ? `${activePkg.name} — ${activePkg.subname}` : 'Litera PSS'}</span></div>
              <div className="flex justify-between"><span>Kategori</span><span className="text-[#14110d]">{prices.tier.label}</span></div>
              <div className="flex justify-between"><span>Bilangan murid</span><span className="text-[#14110d]">{Number(students).toLocaleString('en-MY')}</span></div>
              <div className="flex justify-between border-t border-black/10 pt-1 mt-1">
                {mode === 'first' ? (
                  <>
                    <span>Bayaran tahun pertama{isSoloQuoteModal ? '' : ' (terendah)'}</span>
                    <span className="text-[#14110d] font-bold">
                      {formatRM(
                        isSoloQuoteModal
                          ? packageInitial(activePkg, prices, config).price
                          : Math.min(prices.sistemInitial, prices.bukuInitial)
                      )}
                    </span>
                  </>
                ) : (
                  <>
                    <span>Servis tahunan{isSoloQuoteModal ? '' : ' (terendah)'}</span>
                    <span className="text-[#14110d] font-bold">
                      {formatRM(
                        isSoloQuoteModal
                          ? soloRenewalValue(activePkg, prices)
                          : Math.min(prices.muridPremium, prices.bukuPremium)
                      )}{' '}
                      / tahun
                    </span>
                  </>
                )}
              </div>
            </div>

            <button
              onClick={handlePrint}
              className="w-full bg-[#7c3aed] hover:bg-[#6d28d9] text-white font-semibold py-3 rounded-lg transition flex items-center justify-center gap-2"
            >
              <FileText size={18} /> Cetak / Simpan PDF
            </button>
            <button
              onClick={handleWord}
              className="w-full mt-2 bg-white hover:bg-black/[0.04] text-[#14110d] font-semibold py-3 rounded-lg transition border border-black/15 flex items-center justify-center gap-2"
            >
              <Download size={18} /> Muat Turun Word (.doc) — boleh edit
            </button>
            <p className="text-[11px] text-[#5c554d] text-center mt-2">
              PDF untuk cetak terus · Word (.doc) untuk edit dalam Microsoft Word.
            </p>
          </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

/* =========================================================================
   MAIN — camera controller
   ========================================================================= */
// Persist the user's selections so they survive navigation / remount.
const readPersisted = (key, fallback) => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw == null ? fallback : JSON.parse(raw);
  } catch {
    return fallback;
  }
};
const writePersisted = (key, value) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore */
  }
};

const PackagesPage = () => {
  const [unlocked, setUnlocked] = useState(false);
  const [path, setPath] = useState([]);
  const [dir, setDir] = useState('in');
  // _v2 keys — bumped after the 4-tier change so old stored values don't carry over.
  const [students, setStudentsState] = useState(() =>
    readPersisted('zabra_students_v2', DEFAULT_STUDENTS)
  );
  // null = Lihat Semua
  const [selectedTier, setSelectedTierState] = useState(() =>
    readPersisted('zabra_tier_v2', DEFAULT_TIER)
  );

  const setStudents = useCallback((v) => {
    setStudentsState(v);
    writePersisted('zabra_students_v2', v);
  }, []);
  const setSelectedTier = useCallback((v) => {
    setSelectedTierState(v);
    writePersisted('zabra_tier_v2', v);
  }, []);

  // Global pricing config (session only) + admin panel toggle.
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [adminOpen, setAdminOpen] = useState(false);
  const resetConfig = useCallback(() => setConfig(DEFAULT_CONFIG), []);

  // Export "sebut harga" — stamp date/quote-no once on first open.
  const [exportOpen, setExportOpen] = useState(false);
  const [stamp, setStamp] = useState(null);
  const openExport = useCallback(() => {
    const d = new Date();
    const dateStr = `${d.getDate()} ${MONTHS_MS[d.getMonth()]} ${d.getFullYear()}`;
    const quoteNo = `ZBR/${String(d.getMonth() + 1).padStart(2, '0')}${String(
      d.getDate()
    ).padStart(2, '0')}-${String(d.getHours()).padStart(2, '0')}${String(d.getMinutes()).padStart(2, '0')}`;
    const isoDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate()
    ).padStart(2, '0')}`;
    setStamp({ dateStr, quoteNo, isoDate });
    setExportOpen(true);
  }, []);

  const go = useCallback((next, direction) => {
    setDir(direction);
    setPath(next);
  }, []);
  const back = useCallback(() => {
    setDir('out');
    setPath((p) => {
      // A package sits as a ['pkg', id] pair — pop both so back lands on the
      // parent scene, not a dangling 'pkg' marker.
      if (p.length >= 2 && p[p.length - 2] === 'pkg') return p.slice(0, -2);
      return p.slice(0, -1);
    });
  }, []);
  const home = useCallback(() => {
    setDir('out');
    setPath([]);
  }, []);

  if (!unlocked) return <PasswordGate onUnlock={() => setUnlocked(true)} />;

  // Paths are cumulative breadcrumbs (e.g. ['litera','pkg','sistem']). The active
  // scene is determined by the TAIL: a trailing ['pkg', id] pair, else the last
  // segment. This lets `back` (slice one) return to the parent scene.
  const last = path[path.length - 1];
  const prev = path[path.length - 2];
  let a, b;
  if (prev === 'pkg' && PACKAGES[last]) {
    a = 'pkg';
    b = last;
  } else {
    a = last;
    b = undefined;
  }
  const sceneKey = path.join('/') || 'root';
  const isDetail = path.length > 0;

  // Active package for export: the opened one, else the recommended (sistem).
  const exportPkg = (a === 'pkg' && PACKAGES[b]) ? PACKAGES[b] : PACKAGES.sistem;
  const exportPrices = computePrices(students, selectedTier, config);

  // Build the root scene once so the fallback can reuse the same fully-propped
  // element (a prop-less RootScene would flash "Lihat Semua" / 0 murid).
  const rootScene = (
    <RootScene
      go={go}
      students={students}
      setStudents={setStudents}
      selectedTier={selectedTier}
      setSelectedTier={setSelectedTier}
      config={config}
    />
  );

  let scene;
  if (!a) {
    scene = rootScene;
  } else if (a === 'litera') {
    scene = (
      <LiteraScene
        go={go}
        students={students}
        setStudents={setStudents}
        selectedTier={selectedTier}
        setSelectedTier={setSelectedTier}
        config={config}
      />
    );
  } else if (a === 'pkg' && PACKAGES[b]) {
    const pkg = PACKAGES[b];
    scene = (
      <PackageScene
        pkg={pkg}
        students={students}
        setStudents={setStudents}
        selectedTier={selectedTier}
        setSelectedTier={setSelectedTier}
        config={config}
        onPanel={
          isSoloProduct(pkg)
            ? (key) => go([...path, key === 'percaya' ? 'percaya' : `${pkg.id}-${key}`], 'in')
            : null
        }
      />
    );
  } else if (a === 'kelebihan') {
    scene = <KelebihanScene />;
  } else if (typeof a === 'string' && /-(kelebihan|kenapa|servis|testimoni)$/.test(a) && SOLO_PRODUCTS.includes(a.split('-')[0])) {
    // Solo-product sub-scenes: "<id>-kelebihan|kenapa|servis|testimoni"
    const [pid, key] = [a.split('-')[0], a.split('-').slice(1).join('-')];
    const spkg = PACKAGES[pid];
    const meta = SOLO_QUOTE_META[pid] || {};
    if (key === 'kelebihan') {
      scene = <KelebihanScene showcase={meta.showcase} title={meta.kelebihanTitle} intro={meta.intro} />;
    } else if (key === 'kenapa') {
      scene = <KenapaDigiteraScene />;
    } else if (key === 'servis') {
      scene = <EmptyScene title={`Servis ${spkg.name}`} note="Kandungan servis akan datang." />;
    } else {
      scene = <EmptyScene title={`Kata Mereka — ${spkg.name}`} note="Testimoni akan datang." />;
    }
  } else if (a === 'percaya') {
    scene = <KenapaPercayaScene />;
  } else if (a === 'servis') {
    scene = <ServisScene />;
  } else if (a === 'kad') {
    scene = <KadScene config={config} />;
  } else if (a === 'testimoni') {
    scene = <TestimoniScene />;
  } else {
    // Fallback for any unmatched / transient path state.
    scene = rootScene;
  }

  return (
    <div className="monogram-dark fixed inset-0 overflow-hidden bg-gradient-to-br from-[#2e1065] via-[#4c1d95] to-[#3b0764]">
      <div className="pointer-events-none absolute -top-32 -right-32 w-[28rem] h-[28rem] bg-[#7c3aed]/25 rounded-full blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-32 w-[28rem] h-[28rem] bg-[#0ea5e9]/20 rounded-full blur-3xl" />

      {/* top bar */}
      <div className="absolute top-0 inset-x-0 z-30 h-16 px-4 flex items-center justify-between backdrop-blur-md bg-[#1e1b4b]/70 border-b border-white/10">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setAdminOpen(true)}
            className="text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition"
            aria-label="Tetapan harga"
            title="Tetapan harga"
          >
            <Settings size={16} />
          </button>
          {isDetail && (
            <button
              onClick={back}
              className="flex items-center gap-1.5 text-white/90 hover:text-white bg-white/10 hover:bg-white/20 rounded-full px-4 py-2 text-base font-semibold transition"
            >
              <ArrowLeft size={16} /> Kembali
            </button>
          )}
          {path.length > 1 && (
            <button
              onClick={home}
              className="text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition"
              aria-label="Utama"
            >
              <Home size={16} />
            </button>
          )}
        </div>
        <Link
          to="/"
          className="absolute left-1/2 -translate-x-1/2 hover:opacity-80 transition"
          title="Kembali ke laman utama"
        >
          <span className="flex items-center bg-white rounded-full px-4 py-1.5 shadow-md">
            <img src={LOGO_URL} alt="Zabra Solutions" className="h-8 md:h-10 w-auto" />
          </span>
        </Link>
        <div className="flex items-center gap-2">
          <button
            onClick={openExport}
            className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white text-base font-semibold py-2 px-4 rounded-full transition"
            title="Jana sebut harga"
          >
            <FileText size={16} /> <span className="hidden sm:inline">Sebut Harga</span>
          </button>
          <a
            href={CONTACT_WA}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex bg-[#7c3aed] hover:bg-[#6d28d9] text-white text-base font-semibold py-2 px-5 rounded-full transition"
          >
            Hubungi Kami
          </a>
        </div>
      </div>

      {/* scene viewport */}
      <div className="absolute inset-0 pt-20 pb-16 px-4 flex items-center justify-center overflow-y-auto">
        <AnimatePresence mode="wait" custom={dir}>
          <motion.div
            key={sceneKey}
            custom={dir}
            variants={sceneVariants}
            initial="enter"
            animate="center"
            exit="exit"
            className="w-full flex items-center justify-center my-auto"
          >
            {scene}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Global pricing settings panel */}
      <AdminPanel
        open={adminOpen}
        onClose={() => setAdminOpen(false)}
        config={config}
        setConfig={setConfig}
        resetConfig={resetConfig}
      />

      {/* Sebut Harga export */}
      {stamp && (
        <ExportModal
          open={exportOpen}
          onClose={() => setExportOpen(false)}
          pkg={exportPkg}
          students={students}
          prices={exportPrices}
          config={config}
          dateStr={stamp.dateStr}
          quoteNo={stamp.quoteNo}
          isoDate={stamp.isoDate}
        />
      )}
    </div>
  );
};

export default PackagesPage;
